<template>
  <div>
    <!-------------------------- MAP ---------------------------->
    <l-map ref="map" v-bind:style="mapStyling" :zoom="10" :minZoom="4" :maxZoom="18" :maxBounds="[
      [-90, -180],
      [90, 180],
    ]" :center="center" :options="mapOptions" @update:zoom="zoomUpdated" @update:bounds="boundsUpdated">
      <l-control position="topright" id="v-step-0">
        <button data-tooltip="Left tooltip content" v-bind:class="refreshLoading
          ? 'button is-small is-loading is-success is-outlined is-rounded is-inverted'
          : 'button is-small is-success is-outlined is-rounded is-inverted is-hovered'
          " @click="refresh">
          <span class="icon">
            <i class="fas fa-redo has-text-success"></i>
          </span>
        </button>
      </l-control>
      <l-control-zoom position="topright" class="leaflet-control-zoom"></l-control-zoom>
      <l-tile-layer :url="urlSmooth"></l-tile-layer>
      <l-control position="bottomright">
        <QueuePopUp :showQueuePopUp="showQueuePopUp" :queue="queue" :currentAction="currentAction" :busy="busy"
          @removeActionInQueue="removeActionInQueue" @closeModal="showQueuePopUp = false" v-if="mappingLoaded" />
      </l-control>
    </l-map>
    <!--------------------------  MAP END ----------------------------->
    <!--------------------------  TOOLBAR START ----------------------->
    <div style="
        position: fixed;
        bottom: 0;
        z-index: 1000;
        left: 0;
        right: 0;
        background-color: rgba(255, 255, 255, 0.8);
      " class="is-centered is-mobile is-flex is-flex-direction-row has-text-centered is-justify-content-center py-3">
      <div class="select is-small is-success mx-2">
        <select style="width: 220px" id="v-step-1" @change="selectRegion" :disabled="retrievingFSA">
          <option value="ALL REGIONS" selected>All Regions</option>
          <option v-for="option in regions" :key="option.id" :value="option.id">
            {{ option.name }}
          </option>
        </select>
      </div>
      <!-- Shape Type Filter -->
      <div class="select is-small is-success mx-2">
        <select style="width: 150px" v-model="selectedShapeType"
          :disabled="retrievingFSA || $store.state.region === 'ALL REGIONS'"
          :title="$store.state.region === 'ALL REGIONS' ? 'Select a specific region to filter shapes by type' : 'Filter shapes by type (geofence, distance, billable, payable)'">
          <option value="ALL_TYPES">All Shape Types</option>
          <option value="geofence">Geofence</option>
          <option value="distance">Distance</option>
          <option value="billable">Billable</option>
          <option value="payable">Payable</option>
        </select>
      </div>
      <!-- Client/Contractor Filter (only for billable/payable types) -->
      <div v-if="selectedShapeType === 'billable' || selectedShapeType === 'payable'" class="mx-2">
        <button class="custom-filter-button" :disabled="retrievingFSA || $store.state.region === 'ALL REGIONS'"
          @click="openClientSelectorModal"
          :title="selectedShapeType === 'billable' ? 'Select clients to filter' : 'Select contractors to filter'">
          <span>{{ selectedShapeType === 'billable' ? 'Select Clients' : 'Select Contractors' }}</span>
          <span v-if="selectedClients.length > 0" class="tag is-small is-success ml-2">
            {{ selectedClients.length }}
          </span>
        </button>
      </div>
      <button v-if="
        selectableFSAMode &&
        selectedFSAToAdd.length > 0 &&
        !editSelectableFSAMode
      " class="button is-small is-success mx-2 is-outlined" @click="openAddFSAShapeZoneModal">
        Confirm FSA Zones
      </button>
      <button v-if="
        selectableFSAMode &&
        selectedFSAToAdd.length > 0 &&
        editSelectableFSAMode
      " class="button is-small is-success mx-2 is-outlined" @click="openEditFSAShapeZoneModal">
        Save FSA Zones
      </button>
      <button class="is-small is-success button mx-2" role="navigation" aria-label="dropdown navigation">
        <div class="navbar-item has-dropdown-up is-hoverable is-small">
          <button class="is-small is-success button" id="v-step-3">Menu</button>
          <div class="navbar-dropdown">
            <a class="navbar-item" @click="showExportModal = true" v-if="!retrievingFSA">
              Export Data
            </a>
            <a class="navbar-item" @click="showExportServiceModal = true" v-if="!retrievingFSA && client === 'miele'">
              Export For Service
            </a>
            <a v-if="client === 'miele' && !retrievingFSA" class="navbar-item" @click="showExportServiceModal = true">
              Export For Miele Professionals
            </a>
            <a class="navbar-item" @click="showImportModal = true">
              Import Data
            </a>
            <hr class="navbar-divider" />
            <a class="navbar-item" @click="showSharedViews = true" v-if="
              !selectableFSAMode && !editSelectableFSAMode && !FSAConfirmMode
            ">
              Shared Views
            </a>
            <a class="navbar-item" @click="showRegionList = true" v-if="
              !selectableFSAMode && !editSelectableFSAMode && !FSAConfirmMode
            ">
              Regions
            </a>
            <a class="navbar-item" @click="showZoneListModal = true" v-if="
              !selectableFSAMode && !editSelectableFSAMode && !FSAConfirmMode
            ">
              Manage Zones
            </a>
            <a class="navbar-item" @click="showRouteHubModal = !showRouteHubModal">
              Manage Lanes
            </a>
            <a class="navbar-item" @click="showHubModal = true"
              v-if="!selectableFSAMode && !editSelectableFSAMode && !FSAConfirmMode">
              Manage Reference Points
            </a>
            <hr class="navbar-divider" />
            <a class="navbar-item" v-if="
              !addMoreShapeMode && !deletePartShapeMode && !FSAConfirmMode
            " @click="
              selectableFSAMode
                ? closeSelectableFSAMode(false)
                : selectableFSAModeOn(false)
              ">
              Toggle FSAs visibility
            </a>
            <a class="navbar-item" @click="showFSASelector = true">
              Switch FSA Province
            </a>
            <hr class="navbar-divider" />
            <a class="navbar-item" @click="$router.push('/home')">
              Toggle Environment
            </a>
          </div>
        </div>
      </button>
    </div>
    <!--------------------------  TOOLBAR - end ---------------------------->
    <notifications group="fsa" position="bottom left" />
    <notifications group="export" position="top left" class="export_notification" />
    <notifications group="zones" position="bottom right" />
    <notifications group="lanes" position="bottom center" />
    <notifications group="postal_code_search" position="top right" class="postal-code-search" />
    <ZoneList @renderMap="renderZonesToMap(false, $store.state.region)" :showZoneListModal="showZoneListModal"
      @closeModal="showZoneListModal = false" v-if="mappingLoaded && showZoneListModal" />
    <SharedViews :showSharedViews="showSharedViews" :regions="regions" @closeModal="showSharedViews = false"
      v-if="mappingLoaded && showSharedViews" />
    <RegionList @renderMap="renderZonesToMap(false, $store.state.region)" :showRegionList="showRegionList"
      @closeModal="showRegionList = false" v-if="mappingLoaded && showRegionList" />
    <ChangeRegionModal :showChangingRegionModal="showChangingRegionModal" @closeModal="showChangingRegionModal = false"
      v-if="mappingLoaded && showChangingRegionModal" />
    <History :showHistory="showHistoryModal" @closeModal="showHistoryModal = false"
      v-if="mappingLoaded && showHistoryModal" />
    <HubRouteTable :showRouteHubModal="showRouteHubModal" @closeModal="showRouteHubModal = false"
      v-if="mappingLoaded && showRouteHubModal" />
    <FSASelector :showFSASelector="showFSASelector" :retrievingFSA="retrievingFSA" @closeModal="showFSASelector = false"
      @selectFSAProvince="selectFSAProvince" @closeSelectableFSAMode="closeSelectableFSAMode"
      v-if="mappingLoaded && showFSASelector" />
    <ExportModal :showExportModal="showExportModal" @closeModal="showExportModal = false"
      v-if="mappingLoaded && showExportModal" />
    <ExportServiceModal :showExportServiceModal="showExportServiceModal" @closeModal="showExportServiceModal = false"
      v-if="mappingLoaded && showExportServiceModal" />
    <ImportModal :showImportModal="showImportModal" @closeModal="showImportModal = false"
      v-if="mappingLoaded && showImportModal" />
    <!-- <v-tour name="myTour" :steps="steps"></v-tour> -->
    <LaneWizard :show="showLaneWizard" :hub-ids="laneWizardHubIds" :lane-data="laneWizardData"
      :is-editing="!!laneWizardData" @close="showLaneWizard = false" @laneSaved="handleLaneSaved"
      v-if="mappingLoaded && showLaneWizard" />
    <HubTable :showHubModal="showHubModal" @closeModal="showHubModal = false" v-if="mappingLoaded && showHubModal" />

    <!-- Client/Contractor Selector Modal -->
    <div v-if="showClientSelectorModal" class="modal is-active" style="z-index: 999999;">
      <div class="modal-background" @click="closeClientSelectorModal"></div>
      <div class="modal-card" style="z-index: 1000000; height: 500px;">
        <header class="modal-card-head">
          <p class="modal-card-title">
            {{ selectedShapeType === 'billable' ? 'Select Clients' : 'Select Contractors' }}
          </p>
          <button class="delete" aria-label="close" @click="closeClientSelectorModal"></button>
        </header>
        <section class="modal-card-body">
          <div class="content">
            <p class="mb-4">
              Select {{ selectedShapeType === 'billable' ? 'clients' : 'contractors' }} to filter {{ selectedShapeType
              }} shapes.
              Only shapes that have the selected {{ selectedShapeType === 'billable' ? 'clients' : 'contractors' }} will
              be displayed on the map.
            </p>
            <div class="mb-5">
              <GlobalSelector v-model="tempSelectedClients"
                :label="selectedShapeType === 'billable' ? 'Clients' : 'Contractors'"
                :placeholder="selectedShapeType === 'billable' ? 'Select clients...' : 'Select contractors...'"
                :disabled="false" :dataSource="selectedShapeType === 'billable' ? 'clients' : 'contractors'"
                type="multi" :showSearch="true" />
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-small is-success" @click="applyClientFilter">
            Apply Filter
          </button>
          <button class="button is-small" @click="closeClientSelectorModal">
            Cancel
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>
<script>
import Vue from "vue";
import axios from "axios";
import { LMap, LTileLayer, LControlZoom, LControl } from "vue2-leaflet";
import { GoogleProvider, SearchControl } from "leaflet-geosearch";
import { mapState, mapGetters } from "vuex";
import centerMass from "@turf/center-of-mass";
import { polygon as polygonTurf, circle as circleTurf } from "@turf/turf";
import AddShapeModal from "@/components/AddShapeModal.vue";
import AddMarkerZoneModal from "@/components/AddMarkerZoneModal.vue";
import EditMarkerZoneModal from "@/components/EditMarkerZoneModal.vue";
import PostalCodeSearchPopup from "@/components/PostalCodeSearchPopup.vue";
import LaneReview from "@/components/LaneReview.vue";
import AddFSAZoneModal from "@/components/AddFSAZoneModal.vue";
import EditFSAZoneModal from "@/components/EditFSAZoneModal.vue";
import EditShape from "../utils/components/EditShape";
import {
  taxProvincesCollection,
  hubsCollection,
  zonesInfoCollection,
  regionsCollection,
  configurationsCollection,
  sharedViewsCollection,
  vendorsCollection,
  globalUnitsCollection,
  auth,
  lanesCollection,
  fetchContractors,
  mappingShapesCollection
} from "../store/fireStore";
// Firebase v8 syntax - no need to import query, where, onSnapshot separately
import {
  getZones,
  insertZone,
  deleteZone,
  getFSA,
  getPostalCode,
  EditShapeZone,
  updatePostalCode,
  editZoneCoordinatesMultiple,
  findOne,
  syncAllZonesToFirestore,
} from "../store/mongoStore";
import { regionCenter, geoJsonFeature } from "./utils";
import * as leafletPip from "@mapbox/leaflet-pip";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-toolbar";
import "leaflet.markercluster";
import "leaflet-polylinedecorator";
import streamSaver from "streamsaver";
import WorkerAddMoreShape from "worker-loader!../workers/worker_addMoreShape.js";
import WorkerDeletePartShapeMode from "worker-loader!../workers/worker_deletePartShapeMode.js";
import WorkerAddMoreShapeOverlapping from "worker-loader!../workers/worker_addMoreShapeOverlapping.js";
import WorkerAddShape from "worker-loader!../workers/worker_addShape.js";
import WorkerSelectFSA from "worker-loader!../workers/worker_selectFSA.js";
import WorkerCombineSelectedFSA from "worker-loader!../workers/worker_combineSelectedFSA.js";
import WorkerExportPostalCode from "worker-loader!../workers/worker_exportPostalCode.js";
import "overlapping-marker-spiderfier-leaflet/dist/oms";
// Import SVG images for overlapping shapes
import overlapESvg from "@/assets/images/overlap_e.svg";
import overlapDSvg from "@/assets/images/overlap_d.svg";

const OverlappingMarkerSpiderfier = window.OverlappingMarkerSpiderfier;
/* leaflet default _getIconUrl has a bug and so the fix is temp here */
delete window.L.Icon.Default.prototype._getIconUrl;
window.L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});
let workerAddMoreShape;
let workerDeletePartShapeMode;
let workerAddMoreShapeOverlapping;
let workerAddShape;
let workerSelectFSA;
let workerCombineSelectedFSA;
let workerExportPostalCode;
if (window.Worker) {
  workerAddMoreShape = new WorkerAddMoreShape();
  workerDeletePartShapeMode = new WorkerDeletePartShapeMode();
  workerAddMoreShapeOverlapping = new WorkerAddMoreShapeOverlapping();
  workerAddShape = new WorkerAddShape();
  workerSelectFSA = new WorkerSelectFSA();
  workerExportPostalCode = new WorkerExportPostalCode();
  workerCombineSelectedFSA = new WorkerCombineSelectedFSA();
}

export default {
  name: "Map",
  components: {
    FSASelector: () => import("@/components/FSASelector.vue"),
    ZoneList: () => import("@/components/Zone/ZoneListModal.vue"),
    RegionList: () => import("@/components/Region/RegionListModal.vue"),
    SharedViews: () => import("@/components/SharedViews/SharedViews.vue"),
    ChangeRegionModal: () => import("@/components/ChangeRegionModal"),
    History: () => import("@/components/History"),
    QueuePopUp: () => import("@/components/QueuePopUp"),
    HubRouteTable: () => import("@/components/HubRouteTable"),
    ExportModal: () => import("@/components/ExportModal"),
    ExportServiceModal: () => import("@/components/ExportServiceModal"),
    ImportModal: () => import("@/components/ImportModal"),
    LMap,
    LTileLayer,
    LControlZoom,
    LControl,
    LaneWizard: () => import("@/components/LaneWizard"),
    HubTable: () => import("@/components/HubTable"),
    GlobalSelector: () => import("@/components/GlobalSelector"),
  },
  data() {
    return {
      showScrollMessage: false,
      isMac: /Mac|iPod|iPhone|iPad/.test(navigator.platform),
      isMarkerDragging: false,
      client: process.env.VUE_APP_SHYFTBASE_CUSTOMER_ID,
      distzoneListenerUnsubscribe: null,
      lanesListenerUnsubscribe: null,
      steps: [
        {
          target: "#v-step-0", // We're using document.querySelector() under the hood
          header: {
            title: "1 min tour",
          },
          content: `Refresh the map here.`,
        },
        {
          target: "#v-step-1",
          content: "Main regions of your business.",
        },
        {
          target: "#v-step-2",
          content: "Your actions queue up here.",
        },
        {
          target: "#v-step-3",
          content: "Explore the menu to see more options.",
        },
      ],
      spiderfy: null,
      mappingLoaded: false,
      importingLoading: false,
      refreshLoading: false,
      showZoneListModal: false,
      showChangingRegionModal: false,
      showHistoryModal: false,
      showQueuePopUp: false,
      searchLoading: false,
      showRouteHubModal: false,
      showFSASelector: false,
      showSharedViews: false,
      showRegionList: false,
      showImportModal: false,
      showExportModal: false,
      showExportServiceModal: false,
      center: [43.6532, -79.3832],
      bounds: null,
      map: null,
      postalCodeSearch: "",
      drawnZones: [],
      drawnMarkers: [],
      drawnMarkersPostalCodes: [],
      drawnFSA: [],
      zones: [],
      markers: [],
      regions: [],
      selectedFSAProvince: "AB",
      editedZones: [],
      vehicles: [],
      vehicleObj: {},
      drawControl: null,
      markerGroup: null,
      markerGroupFSA: null,
      addDeletePartShapeModeDrawControl: null,
      FSALayerGroup: null,
      FSANameLayerGroup: null,
      FSALayerControl: null,
      retrievingFSA: false,
      allowOverlappingShapes: false, // Toggle for allowing overlapping shapes
      drawnItems: null,
      FSAItems: null,
      postalCodesGroup: null,
      storeMarkers: window.L.layerGroup(),
      supplierMarkers: window.L.layerGroup(),
      manufacturerMarkers: window.L.layerGroup(),
      primaryMarkers: window.L.layerGroup(),
      secondaryMarkers: window.L.layerGroup(),
      centerZoneMarkers: window.L.layerGroup(),
      stemMarkers: window.L.layerGroup(),
      lanesLayerGroup: window.L.layerGroup(),

      selectedFSAToAdd: [],
      toolbarStyling: {
        width: "0vw",
      },
      mapStyling: {
        width: "100vw",
      },
      mapContainerStyling: {
        position: "initial",
      },
      mapOptions: {
        zoomControl: false,
        scrollWheelZoom: false,
        // preferCanvas: true
      },
      selectedShape: null,
      currentSelectedFSAShape: [],
      toBeSaveShapeFSAID: null,
      editShapeSelectedMode: false,
      addMoreShapeMode: false,
      deletePartShapeMode: false,
      selectableFSAMode: false,
      editSelectableFSAMode: false,
      FSAConfirmMode: false,
      postalCodeMarkerShow: false,
      initialLoadRegion: false,
      queue: [],
      busy: false,
      onEditProcess: false,
      currentAction: null,
      urlSmooth: `https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png?api_key=${process.env.VUE_APP_STADIA_API}`,
      urlSmoothDark: `https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png?api_key=${process.env.VUE_APP_STADIA_API}`,
      urlOutdoors: `https://tiles.stadiamaps.com/tiles/outdoors/{z}/{x}/{y}{r}.png?api_key=${process.env.VUE_APP_STADIA_API}`,
      urlOsrmBright: `https://tiles.stadiamaps.com/tiles/osm_bright/{z}/{x}/{y}{r}.png?api_key=${process.env.VUE_APP_STADIA_API}`,
      // At the class level, add a property to store polylines
      polylines: {},
      showLanes: true,
      currentDrawing: null,
      currentDrawingLayer: null,
      selectedHubIds: [],
      tempHubMarker: null,
      selectedHubsForPolyline: [],
      saveLaneControl: null,
      tempPolyline: null,
      showLaneWizard: false,
      laneWizardHubIds: [],
      laneWizardData: null,
      showHubModal: false,
      isInCustomPolylineMode: false,
      customPolylineControl: null,
      hubMarkersInPolyline: [],
      customPolylinePoints: [],
      selectedMarkerElements: new Map(),
      selectedShapeType: "ALL_TYPES", // Add shape type filter property
      selectedClients: [], // GlobalSelector will handle the array format
      showClientSelectorModal: false,
      tempSelectedClients: [],
      mappingShapesUnsubscribe: null, // Firestore listener unsubscribe function
    };
  },
  beforeDestroy() {
    // Add this hook to remove the event listener when the component is destroyed
    if (this.map) {
      const mapContainer = this.map.getContainer();
      mapContainer.removeEventListener("wheel", this.handleWheel);
    }

    // Clean up listeners
    if (this.distzoneListenerUnsubscribe) {
      this.distzoneListenerUnsubscribe();
    }

    if (this.lanesListenerUnsubscribe) {
      this.lanesListenerUnsubscribe();
    }

    // Clean up polyline creation mode resources
    if (this.tempPolyline && this.map) {
      this.map.removeLayer(this.tempPolyline);
      this.tempPolyline = null;
    }

    if (this.saveLaneControl) {
      this.saveLaneControl.remove();
      this.saveLaneControl = null;
    }

    // Clean up custom polyline control
    if (this.customPolylineControl) {
      this.customPolylineControl.remove();
      this.customPolylineControl = null;
    }

    // Clean up MAPPING_SHAPES listener
    if (this.mappingShapesUnsubscribe) {
      this.mappingShapesUnsubscribe();
      this.mappingShapesUnsubscribe = null;
    }
  },
  created() {
    window.addEventListener("beforeunload", (e) => {
      if (this.queue.length > 0 && !this.busy) {
        const confirmationMessage =
          "Queue is in progress. You may lose some of your work if you exit now. Please cancel and remain on the page till your work is saved.";
        (e || window.event).returnValue = confirmationMessage;
        return confirmationMessage;
      }
    });
  },
  watch: {
    hubs(newVal, oldVal) {
      if (!this.region) {
        return;
      }
      // If we have a selected region, filter and display hubs for that region
      if (this.hubs && this.hubs.length > 0) {
        // Filter hubs for the current region and update the map
        // this.refresh()
        const selectedRegion = this.$store.state.region || "ALL REGIONS";
        this.handleHubsChange(selectedRegion);
      }
    },
    /**
     * Watcher for shape type filter changes
     * @param {String} newValue - New filter value
     * @param {String} oldValue - Previous filter value
     */
    selectedShapeType(newValue, oldValue) {
      console.log('=== WATCHER TRIGGERED ===');
      console.log('Shape type filter changed from', oldValue, 'to', newValue);
      if (newValue !== oldValue) {
        // Reset client selection when shape type changes
        this.selectedClients = [];

        // Recreate drawing control based on new shape type
        this.recreateDrawingControlForShapeType(newValue);
        this.setupMappingShapesListener(this.$store.state.region, newValue);
        this.applyShapeTypeFilter();
      }
    },
    lanes(newVal, oldVal) {
      if (newVal.length === 0 && oldVal.length === 0) {
        return;
      }

      if (!this.lanesLayerGroup) this.lanesLayerGroup = window.L.layerGroup();

      // Instead of removing all lanes, only remove lanes that were deleted
      if (oldVal && newVal && this.map && this.lanesLayerGroup) {
        // Find lanes that exist in oldVal but not in newVal (deleted lanes)
        const oldLaneIds = oldVal.map(lane => lane.id);
        const newLaneIds = newVal.map(lane => lane.id);

        // Get IDs of lanes that were removed
        const removedLaneIds = oldLaneIds.filter(id => !newLaneIds.includes(id));

        // Remove only the deleted lanes
        removedLaneIds.forEach(laneId => {
          if (this.polylines[laneId]) {
            this.lanesLayerGroup.removeLayer(this.polylines[laneId]);
            this.lanesLayerGroup.removeLayer(this.polylineArrows[laneId]);
            this.polylines[laneId].unbindPopup();
            delete this.polylines[laneId];
            delete this.polylineArrows[laneId];
          }
        });
      }

      // Process lanes data to create or update polylines
      if (this.hubs && this.hubs.length > 0 && this.lanes && this.lanes.length > 0) {
        const lanesWithHubCoordinates = this.processLanesWithHubs(newVal);

        lanesWithHubCoordinates.forEach(lane => {
          // Extract coordinates for this lane
          const laneCoordinates = lane.data.map(hub => [
            hub.coordinates.lng,
            hub.coordinates.lat
          ]);

          // Only create polyline if we have at least 2 points
          if (laneCoordinates.length >= 2) {
            // convert laneCoordinates to string
            let laneCoordinatesString = laneCoordinates.map(coord => coord.join(',')).join(';');

            // remove the last ";"
            laneCoordinatesString = laneCoordinatesString.slice(0, -1);

            const url = process.env.VUE_APP_OSRM_GATEWAY;

            axios.post(url, {
              mode: 'route',
              geocodes: laneCoordinatesString
            }).then(response => {
              const route = response.data.routes[0];

              // Extract coordinates
              const coordinates = route.geometry.coordinates.map(coord => [coord[1], coord[0]]);

              // Get the original lane data
              const originalLane = { ...(this.lanes.find(l => l.id === lane.id) || {}) };
              const popupLaneData = { ...originalLane };
              // Get the hub objects for this lane
              const laneHubs = popupLaneData.hubs.map(hubId =>
                this.hubs.find(h => h.id === hubId
                )).filter(hub => hub !== undefined);

              popupLaneData.segments = originalLane.segments.map(segment => ({
                ...segment,
                from: laneHubs.find(h => h.id === segment.from),
                to: laneHubs.find(h => h.id === segment.to)
              }));

              const polyline = this.polylines[lane.id] ? this.polylines[lane.id] : new window.L.Polyline(coordinates, {
                color: "#000",
                weight: 2,
                opacity: 1
              });

              // Create a Vue component for the popup
              const ComponentClass = Vue.extend(LaneReview);
              const instance = new ComponentClass({
                propsData: {
                  lane: popupLaneData || {
                    id: lane.id,
                    name: "Undefined",
                    segments: [],
                    totalLeadTime: 0,
                    capacity: 0,
                    cost: 0,
                    freightType: [],
                    timeOfDayRestrictions: "",
                    environmentalConsiderations: [],
                    technologyIntegration: [],
                    platooningRegulations: {
                      allowed: false,
                      minFollowingDistance: 0,
                    }
                  },
                  selectedHubs: laneHubs,
                  currentSegmentIndex: 0,
                  isPopup: true
                },
                methods: {
                  close: () => {
                    polyline.closePopup();
                  },
                  prevSegment: function () {
                    if (this.currentSegmentIndex > 0) {
                      this.currentSegmentIndex--;
                    }
                  },
                  nextSegment: function () {
                    if (this.currentSegmentIndex < this.lane.segments.length - 1) {
                      this.currentSegmentIndex++;
                    }
                  },
                  editLane: () => {
                    polyline.closePopup();
                    this.laneWizardData = originalLane;
                    this.laneWizardHubIds = laneHubs.map(hub => hub.id);
                    this.showLaneWizard = true;
                  },
                }
              });

              // Mount the component
              instance.$mount();

              // Create a custom popup with minimal padding and custom class
              const popup = window.L.popup({
                className: 'lane-review-popup',
                closeButton: true,
                autoPan: true,
                maxWidth: 500,
                minWidth: 400
              });

              // Set the content and bind to polyline
              popup.setContent(instance.$el);
              polyline.unbindPopup();
              polyline.bindPopup(popup);
              // Add mouseover/mouseout events for better UX (optional)
              polyline.on('mouseover', function () {
                this.setStyle({
                  weight: 4,
                  opacity: 1
                });
              });

              polyline.on('mouseout', function () {
                this.setStyle({
                  weight: 2,
                  opacity: 1
                });
              });

              // Store and add the new polyline
              this.polylines[lane.id] = polyline;
              this.lanesLayerGroup.addLayer(polyline);

              const arrowDecorator = window.L.polylineDecorator(polyline, {
                patterns: [
                  {
                    offset: '10%', // Start at 5% of the polyline
                    repeat: '25%', // Repeat every 15% of the polyline length (more frequent)
                    symbol: window.L.Symbol.arrowHead({
                      pixelSize: 18, // Increase size from 12 to 18
                      headAngle: 40, // Slightly adjust angle for better appearance
                      polygon: true, // Fill the arrow
                      pathOptions: {
                        fillOpacity: 1,
                        weight: 0,
                        color: "#000",
                        fillColor: "#000"
                      }
                    })
                  }
                ]
              });

              // Remove previous decorators for this lane if they exist
              if (this.polylineArrows && this.polylineArrows[lane.id]) {
                this.lanesLayerGroup.removeLayer(this.polylineArrows[lane.id]);
              }

              // Store and add the new decorator
              if (!this.polylineArrows) {
                this.polylineArrows = {};
              }
              this.polylineArrows[lane.id] = arrowDecorator;
              this.lanesLayerGroup.addLayer(arrowDecorator);
            })
          }
        });
      }
      // Add the layer group back to the map if we want to show lanes
      if (this.showLanes && this.map) {
        this.lanesLayerGroup.addTo(this.map);
      }
    },
    region(newVal, oldVal) {
      if (newVal === oldVal) {
        return;
      }

      // this.allowOverlappingShapes = false;
      // this.updateIconOverlappingButton();

      // Reset shape type filter when changing to ALL REGIONS
      if (newVal === "ALL REGIONS") {
        this.selectedShapeType = "ALL_TYPES";
        this.selectedClients = [];
      }

      // reset all layerGroup
      this.storeMarkers.clearLayers();
      this.supplierMarkers.clearLayers();
      this.manufacturerMarkers.clearLayers();
      this.primaryMarkers.clearLayers();
      this.secondaryMarkers.clearLayers();
      this.centerZoneMarkers.clearLayers();
      this.stemMarkers.clearLayers();
      this.handleHubsChange(newVal);
      this.updateFsaLayerVisibility(newVal);
      this.updateDrawControlVisibility(newVal);

      // Reset MAPPING_SHAPES listener for the new region
      this.setupMappingShapesListener(newVal);

      if (newVal !== "ALL REGIONS" && newVal) {
        this.center = regionCenter(
          this.regions.filter((r) => r.id === newVal)[0].name
        )
        // Add null check to prevent error when map is not initialized
        if (this.map && typeof this.map.setView === 'function') {
          this.map.setView(this.center, 8);
        }
        // Add null checks for map operations
        if (this.map) {
          if (this.FSALayerGroup) {
            this.map.removeLayer(this.FSALayerGroup);
          }
          if (this.FSANameLayerGroup) {
            this.map.removeLayer(this.FSANameLayerGroup);
          }
          if (this.FSALayerControl) {
            this.map.removeControl(this.FSALayerControl);
          }
        }
      } else {
        this.getUserLocation().then((position) => {
          this.center = [position.coords.latitude, position.coords.longitude];
          // Add null check to prevent error when map is not initialized
          if (this.map && typeof this.map.setView === 'function') {
            this.map.setView(this.center, 5); // Zoom level 13 is just an example, adjust as needed
          }
        })
      }
    },
    queue: {
      async handler(newVal, oldVal) {
        if (newVal && newVal.length > 0 && !this.busy) {
          await this.processQueue();
        }
      },
    },
    showLanes(newVal) {
      if (this.map) {
        if (newVal) {
          this.lanesLayerGroup.addTo(this.map);
        } else {
          this.map.removeLayer(this.lanesLayerGroup);
        }
      }
    }
  },
  methods: {

    /**
     * Sets up Firestore listener for MAPPING_SHAPES collection
     * Listens for changes in the selected region's shapes
     */
    setupMappingShapesListener(region, type = null) {
      try {
        if (this.mappingShapesUnsubscribe) {
          this.mappingShapesUnsubscribe();
          this.mappingShapesUnsubscribe = null;
        }

        if (!region || region === "ALL REGIONS") {
          return;
        }

        const q = type ? mappingShapesCollection.where('Region', '==', region).where('Type', '==', type)
        : mappingShapesCollection.where('Region', '==', region)

        let isInitialLoad = true

        // Listen to all documents in MAPPING_SHAPES collection
        this.mappingShapesUnsubscribe = q.onSnapshot(
          (snapshot) => {
            console.log("onsnapshot called", isInitialLoad)
            if (isInitialLoad) {
              isInitialLoad = false
              return // Skip processing the initial snapshot
            }
            console.log("onsnapshot called")
            this.$notify({
              group: "zones",
              clean: true,
            });
            this.renderZonesToMap(false, region);
          }
          ,
          (error) => {
            console.error('❌ Error listening to MAPPING_SHAPES collection:', error);
          }
        );

        console.log('✅ MAPPING_SHAPES listener set up successfully', region);
      } catch (error) {
        console.error('❌ Error setting up MAPPING_SHAPES listener:', error);
      }
    },
    /**
     * Sets up event listeners to manage marker interactivity during and after drawing
     * Makes markers non-interactive during drawing and restores interactivity after completion
     */
    setupDrawInteractivityControl() {
      // Helper function to disable interactivity for a marker layer group
      const disableMarkerInteractivity = (layerGroup) => {
        if (layerGroup?._layers && Object.keys(layerGroup._layers).length > 0) {
          Object.keys(layerGroup._layers).forEach(key => {
            const marker = layerGroup._layers[key];
            // Set proper interactive properties to make markers non-interactive
            marker.options.draggable = false;
            marker.options.interactive = false;
            if (marker.dragging) {
              marker.dragging.disable();
            }
          });
        }
      };

      // Helper function to enable interactivity for a marker layer group
      const enableMarkerInteractivity = (layerGroup) => {
        if (layerGroup?._layers && Object.keys(layerGroup._layers).length > 0) {
          Object.keys(layerGroup._layers).forEach(key => {
            const marker = layerGroup._layers[key];
            // Restore interactive properties
            marker.options.draggable = true;
            marker.options.interactive = true;
            if (marker.dragging) {
              marker.dragging.enable();
            }
          });
        }
      };

      // Make markers non-interactive when drawing starts
      this.map.on('draw:drawstart', (e) => {
        // Store the current drawing tool/layer
        this.currentDrawingLayer = e.layer;

        // Apply non-interactive settings to all marker groups
        disableMarkerInteractivity(this.primaryMarkers);
        disableMarkerInteractivity(this.secondaryMarkers);
        disableMarkerInteractivity(this.stemMarkers);
        disableMarkerInteractivity(this.storeMarkers);
        disableMarkerInteractivity(this.supplierMarkers);
        disableMarkerInteractivity(this.manufacturerMarkers);
        disableMarkerInteractivity(this.centerZoneMarkers);
      });

      // Restore interactivity when drawing is complete
      this.map.on('draw:created', (e) => {
        const layer = e.layer;

        // Add the created layer to our feature group
        this.drawnItems.addLayer(layer);

        // Make shape-related markers interactive again
        if (layer._markers) {
          layer._markers.forEach(marker => {
            marker.options.draggable = true;
            marker.options.interactive = true;
          });
        }

        // For polygons/polylines with marker groups
        if (layer._markerGroup) {
          layer._markerGroup.eachLayer(marker => {
            marker.options.draggable = true;
            marker.options.interactive = true;
          });
        }

        // Restore interactivity to all marker groups
        enableMarkerInteractivity(this.primaryMarkers);
        enableMarkerInteractivity(this.secondaryMarkers);
        enableMarkerInteractivity(this.stemMarkers);
        enableMarkerInteractivity(this.storeMarkers);
        enableMarkerInteractivity(this.supplierMarkers);
        enableMarkerInteractivity(this.manufacturerMarkers);
        enableMarkerInteractivity(this.centerZoneMarkers);

        // Reset current drawing layer reference
        this.currentDrawingLayer = null;
      });

      // Handle canceled drawings
      this.map.on('draw:drawstop', () => {
        // Reset current drawing layer reference
        this.currentDrawingLayer = null;

        // Restore interactivity to all marker groups
        enableMarkerInteractivity(this.primaryMarkers);
        enableMarkerInteractivity(this.secondaryMarkers);
        enableMarkerInteractivity(this.stemMarkers);
        enableMarkerInteractivity(this.storeMarkers);
        enableMarkerInteractivity(this.supplierMarkers);
        enableMarkerInteractivity(this.manufacturerMarkers);
        enableMarkerInteractivity(this.centerZoneMarkers);
      });

      // Ensure new vertices added during edit are also interactive
      this.map.on('draw:editvertex', (e) => {
        if (e.vertex) {
          e.vertex.options.draggable = true;
          e.vertex.options.interactive = true;
        }
      });
    },
    /**
   * Initializes a custom control for drawing polylines by connecting hub markers
   */
    initCustomPolylineControl() {
      if (!this.map) return;

      // Create a custom control for the polyline drawing
      const CustomPolylineControl = window.L.Control.extend({
        options: {
          position: 'topright'
        },

        onAdd: (map) => {
          const container = window.L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-custom-polyline-control');
          const button = window.L.DomUtil.create('a', 'custom-polyline-button', container);

          button.href = '#';
          button.title = 'Draw route between hubs';
          button.innerHTML = '<i class="fas fa-route" style="line-height: 30px;"></i>';
          button.style.width = '30px';
          button.style.height = '30px';
          button.style.backgroundColor = '#fff';
          button.style.textAlign = 'center';
          button.style.fontSize = '16px';
          button.style.color = 'black';
          button.style.display = 'block';

          window.L.DomEvent.on(button, 'click', window.L.DomEvent.stop);
          window.L.DomEvent.on(button, 'click', () => {
            this.toggleCustomPolylineMode();
          });

          return container;
        }
      });

      this.customPolylineControl = new CustomPolylineControl();
      this.map.addControl(this.customPolylineControl);
    },
    /**
     * Toggles the custom polyline drawing mode
     */
    toggleCustomPolylineMode() {
      this.isInCustomPolylineMode = !this.isInCustomPolylineMode;

      // Change the cursor style to indicate the active mode
      if (this.isInCustomPolylineMode) {
        // Close any open popups before entering drawing mode
        if (this.map && this.map.closePopup) {
          this.map.closePopup();
        }

        // Hide CENTER ZONE markers when entering lane creation mode
        this.hideCenterZoneMarkers();

        this.map._container.style.cursor = 'crosshair';
        this.startCustomPolylineDrawing();

        // Visual feedback - change the button color
        const button = document.querySelector('.custom-polyline-button');
        if (button) {
          button.style.backgroundColor = 'black';
          button.style.color = 'white';
        }

        this.$notify({
          type: "info",
          group: "zones",
          title: "Distribution Zone Lane Drawing Mode",
          text: "Click on distribution zone markers to connect them. Click 'Save Lane' when finished."
        });
      } else {
        this.cancelCustomPolylineDrawing();

        // Show CENTER ZONE markers when exiting lane creation mode
        this.showCenterZoneMarkers();

        // Reset the button appearance
        const button = document.querySelector('.custom-polyline-button');
        if (button) {
          button.style.backgroundColor = '#fff';
          button.style.color = 'black';
        }
      }
    },
    /**
     * Starts the custom polyline drawing mode
     */
    startCustomPolylineDrawing() {
      // Reset any previous drawing state
      this.hubMarkersInPolyline = [];
      this.customPolylinePoints = [];
      this.selectedMarkerElements.clear();

      if (this.tempPolyline) {
        this.map.removeLayer(this.tempPolyline);
        this.tempPolyline = null;
      }

      // Create a blank polyline
      this.tempPolyline = new window.L.Polyline([], {
        color: 'black',
        weight: 3,
        opacity: 0.7,
        dashArray: '5, 10',
      });
      this.map.addLayer(this.tempPolyline);

      // Add save and cancel controls
      this.addCustomPolylineSaveControl();
    },
    /**
     * Handles the click event on a hub marker during custom polyline drawing
     */
    onHubMarkerClick(marker, event, distributionZoneId) {
      if (!this.isInCustomPolylineMode) return;

      // Find the hub that corresponds to this marker
      const hubForMarker = this.hubs.find(hub => hub.DistributionZoneId === distributionZoneId);

      if (!hubForMarker) {
        this.$notify({
          type: "error",
          group: "zones",
          title: "Invalid Distribution Zone",
          text: "Could not find distribution zone data for this marker."
        });
        return;
      }

      // Get hub ID
      const hubId = hubForMarker.DistributionZoneId;
      const hubPoint = hubForMarker.Center;

      // Check if we've already selected this hub
      if (this.hubMarkersInPolyline.includes(hubId)) {
        this.$notify({
          type: "warning",
          group: "zones",
          title: "Distribution Zone Already Selected",
          text: "This distribution zone is already part of the route."
        });
        return;
      }

      // Add the hub to our list
      this.hubMarkersInPolyline.push(hubId);
      this.customPolylinePoints.push([hubPoint._lat, hubPoint._long]);

      // Highlight the marker to show it's selected
      // Instead of changing the icon, add a CSS class to the marker element
      const markerElement = marker.getElement();
      if (markerElement) {
        markerElement.classList.add('hub-marker-selected');

        // Store the original marker in a map for later reset
        if (!this.selectedMarkerElements) {
          this.selectedMarkerElements = new Map();
        }
        this.selectedMarkerElements.set(hubId, markerElement);
      }

      // Update the polyline
      this.updateCustomPolyline();
    },

    /**
     * Updates the custom polyline with the current points
     */
    updateCustomPolyline() {
      if (!this.tempPolyline || this.customPolylinePoints.length < 1) return;

      // Update the polyline with the new points
      this.tempPolyline.setLatLngs(this.customPolylinePoints);
    },

    /**
     * Adds save and cancel controls for the custom polyline
     */
    addCustomPolylineSaveControl() {
      // Create save and cancel controls if they don't exist
      if (!this.saveLaneControl) {
        const SaveLaneControl = window.L.Control.extend({
          options: {
            position: 'bottomright'
          },

          onAdd: (map) => {
            const container = window.L.DomUtil.create('div', 'leaflet-bar leaflet-control custom-polyline-actions');

            // Save button
            const saveButton = window.L.DomUtil.create('a', 'save-polyline-button', container);
            saveButton.href = '#';
            saveButton.title = 'Save Lane';
            saveButton.innerHTML = '<i class="fas fa-save"></i> Save Lane';
            saveButton.style.backgroundColor = 'black';
            saveButton.style.color = 'white';
            saveButton.style.marginRight = '5px';

            // Revert button - add a new button to revert the last selection
            const revertButton = window.L.DomUtil.create('a', 'revert-polyline-button', container);
            revertButton.href = '#';
            revertButton.title = 'Revert Last Selection';
            revertButton.innerHTML = '<i class="fas fa-undo"></i> Revert';
            revertButton.style.backgroundColor = '#006454';
            revertButton.style.color = '#fff';
            saveButton.style.marginRight = '5px';

            // Cancel button
            const cancelButton = window.L.DomUtil.create('a', 'cancel-polyline-button', container);
            cancelButton.href = '#';
            cancelButton.title = 'Cancel';
            cancelButton.innerHTML = '<i class="fas fa-times"></i> Cancel';
            cancelButton.style.backgroundColor = 'white';
            cancelButton.style.color = 'black';

            window.L.DomEvent.on(saveButton, 'click', window.L.DomEvent.stop);
            window.L.DomEvent.on(saveButton, 'click', () => {
              this.saveCustomPolyline();
            });

            // Add event handler for the revert button
            window.L.DomEvent.on(revertButton, 'click', window.L.DomEvent.stop);
            window.L.DomEvent.on(revertButton, 'click', () => {
              this.revertSelectedHubMarker();
            });

            window.L.DomEvent.on(cancelButton, 'click', window.L.DomEvent.stop);
            window.L.DomEvent.on(cancelButton, 'click', () => {
              this.cancelCustomPolylineDrawing();
            });

            return container;
          }
        });

        this.saveLaneControl = new SaveLaneControl();
        this.map.addControl(this.saveLaneControl);
      }
    },

    /**
     * Saves the custom polyline as a lane
     */
    saveCustomPolyline() {
      if (this.hubMarkersInPolyline.length < 2) {
        this.$notify({
          type: "error",
          group: "zones",
          title: "Not Enough Hubs",
          text: "You need to select at least 2 hubs to create a lane."
        });
        return;
      }

      // Load the lane wizard with the selected hubs
      this.laneWizardHubIds = [...this.hubMarkersInPolyline];
      this.laneWizardData = null;
      this.showLaneWizard = true;

      // Clean up
      this.cancelCustomPolylineDrawing();
    },

    /**
     * Hides CENTER ZONE markers during lane creation
     */
    hideCenterZoneMarkers() {
      if (this.centerZoneMarkers) {
        this.map.removeLayer(this.centerZoneMarkers);
      }
    },

    /**
     * Shows CENTER ZONE markers when exiting lane creation
     */
    showCenterZoneMarkers() {
      if (this.centerZoneMarkers && this.map) {
        this.centerZoneMarkers.addTo(this.map);
      }
    },

    /**
     * Cancels the custom polyline drawing mode
     */
    cancelCustomPolylineDrawing() {
      // Reset cursor
      this.map._container.style.cursor = '';
      this.isInCustomPolylineMode = false;

      // Show CENTER ZONE markers when cancelling lane creation mode
      this.showCenterZoneMarkers();

      // Remove the temporary polyline
      if (this.tempPolyline) {
        this.map.removeLayer(this.tempPolyline);
        this.tempPolyline = null;
      }

      // Remove save/cancel controls
      if (this.saveLaneControl) {
        this.map.removeControl(this.saveLaneControl);
        this.saveLaneControl = null;
      }

      // Reset any highlighted markers
      this.resetHighlightedMarkers();

      // Reset the state
      this.hubMarkersInPolyline = [];
      this.customPolylinePoints = [];

      // Reset the marker click handlers to their original behavior

      // Reset the button appearance
      const button = document.querySelector('.custom-polyline-button');
      if (button) {
        button.style.backgroundColor = '#fff';
        button.style.color = 'black';
      }

      // Refresh the map to ensure all markers are properly interactive
      if (this.map) {
        this.map.invalidateSize();
      }
    },

    /**
     * Resets any highlighted markers
     */
    resetHighlightedMarkers() {
      // Remove the highlight class from the stored marker elements
      if (this.selectedMarkerElements && this.selectedMarkerElements.size > 0) {
        this.selectedMarkerElements.forEach((markerElement) => {
          if (markerElement && markerElement.classList) {
            markerElement.classList.remove('hub-marker-selected');
          }
        });
        this.selectedMarkerElements.clear();
      }
    },

    /**
     * Reverts the last selected hub marker in custom polyline mode
     * This method works opposite to onHubMarkerClick by removing the last selected hub
     */
    revertSelectedHubMarker(isSpiderfy = false) {
      if (!this.isInCustomPolylineMode) return;

      // Check if we have any hub markers in the polyline
      if (!this.hubMarkersInPolyline.length) {
        return;
      }

      // Get the last hub ID in the list
      const lastHubId = this.hubMarkersInPolyline[this.hubMarkersInPolyline.length - 1];

      // Get the marker element for this hub
      const markerElement = this.selectedMarkerElements?.get(lastHubId);

      // Remove the highlighting from the marker
      if (markerElement && markerElement.classList) {
        markerElement.classList.remove('hub-marker-selected');
      }

      // Remove this hub ID from the map of selected marker elements
      if (this.selectedMarkerElements?.has(lastHubId)) {
        this.selectedMarkerElements.delete(lastHubId);
      }

      // Remove the last hub from our list
      this.hubMarkersInPolyline.pop();

      // Remove the last point from our polyline points
      this.customPolylinePoints.pop();

      // Update the polyline
      this.updateCustomPolyline();

      // Find the hub that corresponds to this ID to show its name in the notification
      const hubForMarker = this.hubs?.find(hub => hub.DistributionZoneId === lastHubId);

      // Show notification
      if (!isSpiderfy) {
        this.$notify({
          type: "info",
          group: "zones",
          title: "Distribution Zone Removed",
          text: `Removed ${hubForMarker?.DistributionZoneName || 'distribution zone'} from route. Remaining: ${this.hubMarkersInPolyline.length}`
        });
      }
    },

    getUserLocation() {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          console.warn("Geolocation is not supported by this browser");
          // Resolve with default coordinates instead of rejecting
          resolve({ coords: { latitude: 43.6532, longitude: -79.3832 } });
          return;
        }

        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve(position);
          },
          (error) => {
            console.warn("Geolocation error:", error.message);
            // Resolve with default coordinates instead of rejecting
            resolve({ coords: { latitude: 43.6532, longitude: -79.3832 } });
          },
          {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0
          }
        );
      });
    },
    // handleScroll: (event) => {
    //   if (!event.ctrlKey && !event.metaKey) {
    //     alert("Please use the mouse wheel with the CTRL key");
    //   }
    // },
    async processQueue() {
      if (this.queue.length) {
        this.busy = true;
        const currentQueueItem = this.queue.shift();
        const { action, state } = currentQueueItem;
        this.currentAction = action;
        await this.processQueueHandler(action, state);
      } else {
        this.busy = false;
        this.currentAction = null;
      }
    },
    createPostalCodeMarker(lat, lng, addressComponents) {
      const marker = new window.L.Marker([lat, lng], {
        icon: this.getIcon("black", "POSTAL_CODE"),
        riseOnHover: true,
        draggable: false,
      });

      const ComponentClass = Vue.extend(PostalCodeSearchPopup);

      const clientOptions = this.$store.state.vendors.map(vendor => ({
        label: vendor.businessUnit + "***" + vendor.subBusinessUnit,
        value: vendor.Id
      }));

      const instance = new ComponentClass({
        propsData: {
          store: this.$store,
          coordinates: { lat, lng },
          clients: clientOptions,
          region: this.$store.state.region,
          postalCodeData: null,
          addressComponents: addressComponents,
        },
        methods: {
          close: () => {
            marker.closePopup();
          },
        },
      });
      instance.$mount();

      // Listen for close event from the popup component
      instance.$on('close', () => {
        this.cleanupPostalCodeMarker(marker);
      });

      // Listen for save event from the popup component
      instance.$on('save', (data) => {
        // Show success notification
        this.$notify({
          group: "postal_code_search",
          type: "success",
          title: "Postal Code Saved",
          text: `Price for ${data.postalCode} has been saved successfully.`,
          duration: 3000
        });
      });

      // Listen for the popup's close event (triggered by default close button)
      marker.on('popupclose', () => {
        // After a short delay to prevent race conditions
        setTimeout(() => {
          // Check if popup was closed by our custom close button (instance.$emit('close'))
          // If our handleClose wasn't triggered, we need to clean up from here
          if (this.map && this.map._layers[marker._leaflet_id]) {
            this.cleanupPostalCodeMarker(marker);
          }
        }, 100);
      });

      return { marker, instance };
    },

    /**
     * Clean up postal code marker and related resources
     * Used when marker popup is closed by any means
     */
    cleanupPostalCodeMarker(marker) {
      // Close popup if it's still open
      marker.closePopup();

      // Remove marker from map
      if (this.map && this.map._layers[marker._leaflet_id]) {
        this.map.removeLayer(marker);
      }

      // Remove marker from layers
      if (this.postalCodesGroup && this.postalCodesGroup._layers[marker._leaflet_id]) {
        this.postalCodesGroup.removeLayer(marker);
      }

      // Remove marker from spiderfy if present
      if (this.spiderfy) {
        try {
          this.spiderfy.removeMarker(marker);
        } catch (err) {
          console.warn('Error removing marker from spiderfy:', err);
        }
      }

      // Remove from drawn markers if present
      const drawnMarkerIdx = this.drawnMarkersPostalCodes.findIndex(m => m.leafletId === marker._leaflet_id);
      if (drawnMarkerIdx !== -1) {
        this.drawnMarkersPostalCodes.splice(drawnMarkerIdx, 1);
      }

      // Clean up notification if any
      this.$notify({ group: "postal_code_search", clean: true });
    },
    createMarker(lat, lng, isNewHub = false) {
      const marker = new window.L.Marker([lat, lng], {
        icon: this.getIcon("#007D6A", "DEFAULT"),
        riseOnHover: true,
        draggable: true,
      });

      const gjLayer = window.L.geoJson(JSON.parse(geoJsonFeature));
      const firstPointResult = leafletPip.pointInLayer([lng, lat], gjLayer);
      const timeZone = firstPointResult[0].feature.properties.tz_name1st || "";

      const ComponentClass = Vue.extend(AddMarkerZoneModal);
      const instance = new ComponentClass({
        propsData: {
          coordinates: { lat, lng },
          region: this.$store.state.region,
          timeZone: timeZone,
        },
        methods: {
          close: () => {
            marker.closePopup();
          },
        },
      });
      instance.$mount();

      this.setMarkerEventListener(marker, instance, gjLayer, isNewHub);

      return { marker, instance };
    },
    setMarkerEventListener(marker, instance, gjLayer, isNewHub) {
      marker.on("click", (e) => {
        const markerProperties = this.drawnMarkers.find(m => m.leafletId === marker._leaflet_id);
        const distributionZoneId = markerProperties ? markerProperties.id : null;
        if (this.isInCustomPolylineMode) {
          marker.closePopup();
          this.onHubMarkerClick(marker, e, distributionZoneId);
        }
      }
      );
      marker.on("dragstart", () => {
        this.isMarkerDragging = true;
        this.map.closePopup();
      });

      marker.on("dragend", (e) => {
        this.isMarkerDragging = false;
        const { lat, lng } = e.target._latlng;

        const dragendPointResult = leafletPip.pointInLayer([lng, lat], gjLayer);
        instance.timeZone =
          dragendPointResult[0].feature.properties.tz_name1st || "";
        instance.coordinates = { lat, lng };
        instance.$mount();
        const _contentPopup = window.L.popup({
          className: "hub-content-popup",
          closeButton: true,
          autoPan: true,
        })

        _contentPopup.setContent(instance.$el);

        marker.bindPopup(_contentPopup);
        marker.openPopup();
      });
      let closePopupTimeout;
      if (isNewHub) {
        // Add event listener for popup close
        marker.on("popupclose", (e) => {
          // Clear any existing timeout
          if (closePopupTimeout) {
            clearTimeout(closePopupTimeout);
          }

          // Set a new timeout
          closePopupTimeout = setTimeout(() => {
            if (!this.isMarkerDragging && this.map._layers[marker._leaflet_id]) {
              this.map.removeLayer(marker);
            }
          }, 50); // 50ms delay, adjust as needed
          // Prevent the popup from closing immediately
          if (this.isMarkerDragging) {
            e.preventDefault();
          }
        });
      }

      instance.$on("coordinate-change", ({ lat, lng }) => {
        this.centerMapWithOffset(lat, lng);
        this.updateMarkerPosition(marker, lat, lng);
        marker.openPopup();
      });

      // Listen for type changes to update marker icon
      instance.$on("type-change", ({ type, color }) => {
        if (marker && marker.setIcon) {
          const newIcon = this.getIcon(color, type);
          marker.setIcon(newIcon);
        }
      });
    },
    centerMapWithOffset(lat, lng) {
      this.map.panTo([lat, lng]);
      /*
      const popup = this.map._popup;
      if (popup) {
        const px = this.map.project([lat, lng]);
        px.y -= popup._container.clientHeight / 2; // Additional offset for popup above marker
        const latlng = this.map.unproject(px);
        this.map.panTo(latlng);
      } else {
        this.map.panTo([lat, lng]);
      } */
    },
    updateMarkerPosition(marker, lat, lng) {
      const gjLayer = window.L.geoJson(JSON.parse(geoJsonFeature));
      const pointResult = leafletPip.pointInLayer([lng, lat], gjLayer);
      const timeZone = pointResult[0].feature.properties.tz_name1st || "";

      marker.setLatLng([lat, lng]);
      marker._popup.setLatLng([lat, lng]);

      const instance = marker._popup.getContent().__vue__;
      instance.coordinates = { lat, lng };
      instance.timeZone = timeZone;
      instance.$forceUpdate();
    },
    handleWheel(e) {
      e.preventDefault();
      const map = this.$refs.map.mapObject;
      if (e.deltaY < 0) {
        map.zoomIn();
      } else {
        map.zoomOut();
      }
    },
    async processQueueHandler(action, state) {
      await this.processHandler(action, state);
      return setTimeout(() => {
        this.busy = false;
        this.processQueue();
        return Promise.resolve();
      }, 100);
    },
    async processHandler(action, state) {
      if (action === "INSERT_NEW_SHAPE") {
        console.log("insert new shape called", state)
        const { coordinates, shapesID, multi, fsa, distributionZoneId, zoneClients, type, daysOfService, color } = state;
        const newZone = await insertZone(coordinates, shapesID, multi, fsa, distributionZoneId, type, daysOfService, color);
        this.$notify({ group: "zones", clean: true });
        this.$notify({
          group: "zones",
          title: "Processing",
          closeOnClick: false,
          duration: 5000,
          text: "Data processing, please wait...",
        });
        const {
          coordinates: newCoordinates,
          shapesID: newShapesID,
          multi: newMulti,
        } = newZone;
        const newShape = await updatePostalCode(
          newCoordinates,
          newShapesID,
          newMulti,
          distributionZoneId,
          zoneClients,
          [],
          daysOfService || {},
          type || "geofence",
          color
        );
        return Promise.resolve(newShape);
      } else if (action === "DELETE_SHAPE") {
        console.log("delete shape called")
        const { shapeId } = state;
        const { coordinates, isMulti, newZone, distributionZoneId, zoneClients, type } = await deleteZone(shapeId);

        // Handle distance shapes differently - update Firestore instead of postal codes
        if (type === 'distance') {
          try {
            // Get the zone document to update the centers array
            const zoneDoc = await zonesInfoCollection.doc(newZone).get();
            const zoneData = zoneDoc.data();

            if (zoneData && zoneData.centers) {
              // Remove the center with matching distributionZoneId from the centers array
              const centers = zoneData.centers.filter(center => center.id !== distributionZoneId);

              // Update the zone document
              await zonesInfoCollection.doc(newZone).update({
                // Also update the single center field for backward compatibility
                center: centers,
              });

              console.log('Removed center from zone centers array:', distributionZoneId);
            }
          } catch (error) {
            console.error('Error updating zone centers after shape deletion:', error);
          }

          return Promise.resolve({ success: true });
        } else {
          // For non-distance shapes, update postal codes as before
          const updatedPostalCode = await updatePostalCode(
            coordinates,
            newZone,
            isMulti,
            distributionZoneId,
            zoneClients,
            [],
            {},
            type // Use the actual type from the deleted shape
          );
          return Promise.resolve(updatedPostalCode);
        }
      } else if (action === "INSERT_NEW_SHAPE_FROM_EDIT_OVERLAPPING") {
        // TBD: Where is this action used?
        const { coordinates, zoneId, multi, fsa, type, daysOfService } = state;
        const {
          coordinates: newCoordinates,
          isMulti,
          newZone,
        } = await insertZone(coordinates, zoneId, multi, fsa, "", type || "geofence", daysOfService || {});
        const updatedPostalCodes = await updatePostalCode(
          newCoordinates,
          newZone,
          isMulti,
          "",
          [],
          [],
          daysOfService || {},
          type || "geofence"
        );
        return Promise.resolve(updatedPostalCodes);
      } else if (action === "EDIT_COORDINATES") {
        const updatedShape = await this.updateShape("EDIT_COORDINATES", state);
        return Promise.resolve(updatedShape);
      } else if (action === "PROCESS_EDITING_ADD") {
        const { selectedShape, coordinates, drawnZones } = state;
        const currentSelectedShape = await findOne("zones", {
          _id: selectedShape.id,
        });
        const currentSelectedShapeCoordinates = currentSelectedShape.multi
          ? JSON.parse(currentSelectedShape.coordinates).map((shape) => {
            return shape.map((c) => [c[1], c[0]]);
          })
          : JSON.parse(currentSelectedShape.coordinates).map((c) => [
            c[1],
            c[0],
          ]);
        const currentSelectedShapeInvertedOrderCoordinates = JSON.parse(
          currentSelectedShape.coordinates
        );
        const selectedShapeUpdated = {
          ...currentSelectedShape,
          coordinates: currentSelectedShapeCoordinates,
          invertedCoordinates: currentSelectedShapeInvertedOrderCoordinates,
          shapeId: currentSelectedShape._id.toString(),
          id: currentSelectedShape._id.toString(),
        };
        workerAddMoreShape.postMessage({
          selectedShape: selectedShapeUpdated,
          coordinates,
          drawnZones,
          drawnFSA: this.drawnFSA,
        });
        return Promise.resolve();
      } else if (action === "PROCESS_EDITING_REMOVE") {
        const { selectedShape, coordinates, drawnZones } = state;
        const currentSelectedShape = await findOne("zones", {
          _id: selectedShape.id,
        });
        const currentSelectedShapeCoordinates = currentSelectedShape.multi
          ? JSON.parse(currentSelectedShape.coordinates).map((shape) => {
            return shape.map((c) => [c[1], c[0]]);
          })
          : JSON.parse(currentSelectedShape.coordinates).map((c) => [
            c[1],
            c[0],
          ]);
        const currentSelectedShapeInvertedOrderCoordinates = JSON.parse(
          currentSelectedShape.coordinates
        );
        const selectedShapeUpdated = {
          ...currentSelectedShape,
          coordinates: currentSelectedShapeCoordinates,
          invertedCoordinates: currentSelectedShapeInvertedOrderCoordinates,
          shapeId: currentSelectedShape._id.toString(),
          id: currentSelectedShape._id.toString(),
        };
        workerDeletePartShapeMode.postMessage({
          selectedShape: selectedShapeUpdated,
          distributionZoneId: currentSelectedShape.distributionZoneId,
          coordinates,
          drawnFSA: this.drawnFSA,
        });
        return Promise.resolve();
      } else if (action === "EDIT_MULTIPLE_ZONE_FSA") {
        const { newShapes, currentShape, shapeType } = state;
        this.onEditProcess = true;
        await editZoneCoordinatesMultiple(
          newShapes,
          currentShape,
          shapeType
        ).then((res) => {
          this.onEditProcess = false;
          return Promise.resolve();
        });
      }
    },
    async updateShape(action, state) {
      const { coordinates, shapeId, multi, fsa, distributionZoneId } = state;
      const res = await EditShapeZone({
        coordinates,
        shapeId,
        multi,
        fsa,
        distributionZoneId,
      });
      this.onEditProcess = false;
      const {
        currentZoneCoordinates,
        currentZoneMulti,
        newZoneCoordinates,
        newShapesId,
        newMulti,
      } = res;
      const firstUpdate = await updatePostalCode(
        currentZoneCoordinates,
        "",
        currentZoneMulti,
        "",
        [],
        [],
        {},
        "geofence" // Default type for coordinate updates
      );
      const lastUpdate = await updatePostalCode(
        newZoneCoordinates,
        newShapesId,
        newMulti,
        distributionZoneId,
        [],
        [],
        {},
        "geofence" // Default type for coordinate updates
      );
      return { lastUpdate, firstUpdate };
      // return { ...res };
    },
    /* Select the province for which to load the FSAs */
    async selectFSAProvince(selectedFSAProvince) {
      if (this.selectedFSAProvince === "") {
        this.$notify({
          type: "warn",
          group: "export",
          title: "No selected provice",
          text: "Please select a region",
          duration: 20000,
        });
        return;
      }
      this.showChangingRegionModal = true;
      this.closeSelectableFSAMode(true);
      this.center = regionCenter(selectedFSAProvince);
      this.map.removeLayer(this.FSALayerGroup);
      this.map.removeLayer(this.FSANameLayerGroup);
      this.map.removeControl(this.FSALayerControl);
      this.renderFSAToMap(selectedFSAProvince);
      this.showFSASelector = false;
    },
    /* Update map zoon */
    zoomUpdated(zoom) {
      this.zoom = zoom;
      this.mapContainerStyling = {
        position: "initial",
      };
    },
    /* Update map bounds */
    boundsUpdated(bounds) {
      this.bounds = bounds;
      this.mapContainerStyling = {
        position: "initial",
      };
    },
    /**
     * @param  {e} event - value of selected region
     */
    async selectRegion(e) {
      const newRegion = e.target.value;
      this.$store.dispatch("setRegion", newRegion);
      this.zones = this.$store.state.zoneInformations.filter(
        (doc) => doc.environment === this.$store.state.environment
      );
      // Reset shape type filter when changing regions
      this.selectedShapeType = "ALL_TYPES";
      this.selectedClients = [];
      // this is to avoid getting the fsa of the same province
      const fsaRegion = this.regions.filter((r) => r.id === newRegion)[0].fsaRegion;
      Promise.all([this.renderFSAToMap(fsaRegion), this.renderZonesToMap(true, newRegion)]);
    },
    /**
     * Return Icon indicator for DISTRIBUTIONZONES and ZONE TYPES
     * @param {string} color - The color of the icon
     * @param {string} type - The type of zone or distribution zone
     * @returns {Object} - Leaflet divIcon object
     */
    getIcon(color, type = "DEFAULT") {
      let svgString;

      // Distribution Zone Types
      if (type === "STORE") {
        svgString = `<svg style="fill:${color}" width="20px" height="20px" viewBox="0 0 20 20"><path d="M13 17a2 2 0 1 0 3.999.001A2 2 0 0 0 13 17zM3 17a2 2 0 1 0 4 0 2 2 0 0 0-4 0zm3.547-4.828L17.615 9.01A.564.564 0 0 0 18 8.5V3H4V1.4c0-.22-.181-.4-.399-.4H.399A.401.401 0 0 0 0 1.4V3h2l1.91 8.957.09.943v1.649c0 .219.18.4.4.4h13.2c.22 0 .4-.182.4-.4V13H6.752c-1.15 0-1.174-.551-.205-.828z"></path></svg>`;
      } else if (type === "SUPPLIER" || type === "MANUFACTURER") {
        svgString = `<svg style="fill:${color}" width="22px" height="22px" viewBox="0 0 33 32"><path d="M32.45,8.44,22,15.3V9.51a1,1,0,0,0-1.63-.78L14.07,14H10V4.06L4,2.71V14H2V31a1,1,0,0,0,1,1H33a1,1,0,0,0,1-1V9.27A1,1,0,0,0,32.45,8.44ZM14,29H6V27h8Zm0-4H6V23h8Zm0-4H6V19h8Zm8,8H20V26h2Zm0-6H20V20h2Zm0-6H20V20h2Zm4,6H24V26h2Zm0-6H24V20h2Zm4,6H28V26h2Zm0-6H28V20h2Z"></path></svg>`;
      } else if (type === "POSTAL_CODE") {
        svgString = `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 24 36">
  <path d="M12 0C6.48 0 2 4.48 2 10c0 7.5 10 26 10 26s10-18.5 10-26C22 4.48 17.52 0 12 0zm0 14.5c-2.48 0-4.5-2.02-4.5-4.5S9.52 5.5 12 5.5s4.5 2.02 4.5 4.5-2.02 4.5-4.5 4.5z" fill=${color}/>
</svg>`;
      } else if (type === "CENTER ZONE") {
        svgString = `<svg style="fill:${color || "black"}" width="22px" height="22px" viewBox="0 0 20 20"><g><circle cx="10" cy="10" r="9" stroke="${color || "black"}" stroke-width="1" fill="none"/><rect x="6" y="6" width="2" height="2"></rect><rect x="9" y="6" width="2" height="2"></rect><rect x="12" y="6" width="2" height="2"></rect><rect x="6" y="9" width="2" height="2"></rect><rect x="9" y="9" width="2" height="2"></rect><rect x="12" y="9" width="2" height="2"></rect><rect x="6" y="12" width="2" height="2"></rect><rect x="9" y="12" width="2" height="2"></rect><rect x="12" y="12" width="2" height="2"></rect></g></svg>`;
      } else {
        // Default warehouse icon for other types
        svgString = `<svg style="fill:${color || "black"}" width="24px" height="24px" viewBox="0 0 20 20"><g><polygon points="16,5 9,1 2,5 2,17 4,17 4,7 14,7 14,17 16,17"></polygon><rect x="5" y="15" width="2" height="2"></rect><rect x="8" y="15" width="2" height="2"></rect><rect x="11" y="15" width="2" height="2"></rect><rect x="5" y="12" width="2" height="2"></rect><rect x="8" y="12" width="2" height="2"></rect><rect x="11" y="12" width="2" height="2"></rect><rect x="5" y="9" width="2" height="2"></rect><rect x="8" y="9" width="2" height="2"></rect></g></svg>`;
      }

      return window.L.divIcon({
        className: "my-custom-pin",
        html: svgString,
        iconSize: [32, 32], // Set your icon size here
        iconAnchor: [16, 16], // Set to half of iconSize to center the marker
        popupAnchor: [0, -16]
      });
    },
    /* adding markers to their own type layer so they can be filtered */
    addMarkerToLayerGroup(marker, type) {
      if (!this.storeMarkers) this.storeMarkers = window.L.layerGroup();
      if (!this.supplierMarkers) this.supplierMarkers = window.L.layerGroup();
      if (!this.manufacturerMarkers) this.manufacturerMarkers = window.L.layerGroup();
      if (!this.primaryMarkers) this.primaryMarkers = window.L.layerGroup();
      if (!this.secondaryMarkers) this.secondaryMarkers = window.L.layerGroup();
      if (!this.stemMarkers) this.stemMarkers = window.L.layerGroup();
      if (!this.centerZoneMarkers) this.centerZoneMarkers = window.L.layerGroup();

      if (type === "STORE" || type === 3) {
        this.storeMarkers.addLayer(marker);
      } else if (type === "SUPPLIER" || type === 4) {
        this.supplierMarkers.addLayer(marker);
      } else if (type === "MANUFACTURER" || type === 5) {
        this.manufacturerMarkers.addLayer(marker);
      } else if (type === "PRIMARY" || type === 0) {
        this.primaryMarkers.addLayer(marker);
      } else if (type === "SECONDARY" || type === 1) {
        this.secondaryMarkers.addLayer(marker);
      } else if (type === "STEM" || type === 2) {
        this.stemMarkers.addLayer(marker);
      } else if (type === "CENTER ZONE") {
        this.centerZoneMarkers.addLayer(marker);
      }
    },
    /**
     * If the app is on edit mode. It reverts back to default mode
     */
    closeEditCurrentShapeMode() {
      this.editShapeSelectedMode = false;
      this.selectedShape = null;
      this.map.addControl(this.drawControl);
      this.map.closePopup();
    },
    /**
     * @params toAdd - checks if its adding or delete mode
     * Changes mode to Edit mode
     */
    editCurrentShapeModeOn(toAdd) {
      this.map.addControl(this.addDeletePartShapeModeDrawControl);
      this.map.removeControl(this.drawControl);
      this.map.closePopup();
      // new window.L.Draw.Rectangle(this.map).enable()
      if (toAdd) {
        this.addMoreShapeMode = true;
      } else {
        this.deletePartShapeMode = true;
      }
      // Make FSA layer button unclickable
      const fsaTargetElement = document.querySelector(
        ".leaflet-control-layers-overlays > :first-child"
      );
      fsaTargetElement.style.pointerEvents = "none";
    },
    /**
     * Reverts to normal mode if the app is currently on edit mode
     */
    closeAddDeletePartShapeMode() {
      // new window.L.Draw.Rectangle(this.map).disable()
      this.deletePartShapeMode = false;
      this.addMoreShapeMode = false;
      this.selectedShape = null;
      this.map.removeControl(this.addDeletePartShapeModeDrawControl);
      this.map.addControl(this.drawControl);
      const fsaTargetElement = document.querySelector(
        ".leaflet-control-layers-overlays > :first-child"
      );
      fsaTargetElement.style.pointerEvents = "";
    },

    /**
     * Add tooltips to all polygons with smart positioning
     * Inner shapes have tooltip at center, outer shapes have tooltip at top left
     * @param {Array} allPolygons - Array of polygon objects with metadata
     */
    addTooltipsToPolygons(allPolygons) {
      // First, determine which shapes are inner and which are outer
      // We'll use area as a heuristic - smaller shapes are more likely to be inner
      const polygonsWithArea = allPolygons.map(shape => {
        const area = this.calculatePolygonArea(shape.polygon);
        return { ...shape, area };
      });

      // Sort by area (ascending) so smaller (likely inner) shapes are processed first
      polygonsWithArea.sort((a, b) => a.area - b.area);
      // Group polygons into nested groups
      const polygonGroups = this.groupNestedPolygons(polygonsWithArea);

      // Process all polygons to add tooltips
      polygonsWithArea.forEach(({ polygon, zone, hub, index, area }, arrayIndex) => {
        const tooltipContent = `<div class="tooltip-content">
            <span class="zone-name">${zone || ""}</span>
            ${zone && hub ? '<span class="separator"> / </span>' : ""}
            <span class="hub-name">${hub ? hub.DistributionZoneName || "" : ""}</span>
          </div>`;

        // Get the bounds of the polygon
        const bounds = polygon?.getBounds();
        if (!bounds) return;

        // Default behavior: Center tooltip for inner shapes
        let tooltipOffset = [0, 0];
        let tooltipDirection = "center";

        // Find which group this polygon belongs to and if it's the innermost in that group
        const groupInfo = this.findPolygonGroupInfo(polygon, polygonGroups);
        const isInnermostShape = groupInfo?.isInnermost || false;

        // Check if the polygon has holes - if it does, we shouldn't place tooltip in center
        const hasHoles = this.polygonHasHoles(polygon);

        const isToolTipOnCenter = isInnermostShape && !hasHoles
        if (isToolTipOnCenter) {
          // For innermost shape without holes, center the tooltip
          tooltipDirection = "center";
          tooltipOffset = [0, 0]; // No offset for center positioning
        } else {
          // For shapes with holes or outer shapes, position outside at the top-left corner

          // Calculate a fixed pixel offset from the northwest corner
          // Simple fixed offset is sufficient since we're positioning at the highest point
          const baseOffsetX = 0; // No horizontal offset needed
          const baseOffsetY = -10; // Small negative offset to position slightly above the shape

          // Use "auto" direction for tooltip positioning
          tooltipDirection = "center"; // Position tooltip above the point (pointing down)

          // Set simple offset
          tooltipOffset = [baseOffsetX, baseOffsetY];

          // For very large shapes, we might want a slightly larger offset
          if (area > 1) {
            const sizeFactor = Math.min(2, Math.sqrt(area) / 15);
            tooltipOffset = [
              tooltipOffset[0] * sizeFactor,
              tooltipOffset[1] * sizeFactor
            ];
          }
        }

        // Position tooltip with appropriate offset and direction
        const tooltipOptions = {
          permanent: true,
          direction: tooltipDirection,
          className: "zone-tooltip" + (isToolTipOnCenter ? " innermost-tooltip" : " other-tooltip"),
          offset: tooltipOffset
        };

        // For non-innermost shapes, we can also try to position the tooltip at a specific point
        if (!isToolTipOnCenter) {
          // Get the center of the polygon for horizontal positioning
          const center = bounds.getCenter();

          // Find the true northmost point of the polygon by examining all vertices
          // This ensures we're placing the tooltip at the actual highest point
          const latLngs = polygon.getLatLngs();
          let highestLat = -90; // Initialize with minimum possible latitude
          let highestLong = -180;
          let validPointsFound = false;
          let totalPoints = 0;

          // Process coordinates - find highest point and count points in a single pass
          const processCoordinates = (coords) => {
            if (!coords) return 0;

            let count = 0;
            if (Array.isArray(coords)) {
              if (coords.length > 0 && coords[0] instanceof window.L.LatLng) {
                // It's an array of LatLng objects (simple polygon)
                coords.forEach(point => {
                  if (point && typeof point.lat === 'number') {
                    validPointsFound = true;
                    count++;
                    if (point.lat > highestLat) {
                      highestLat = point.lat;
                      highestLong = point.lng;
                    }
                  }
                });
              } else {
                // It's a nested structure (multipolygon or polygon with holes)
                coords.forEach(innerCoords => {
                  count += processCoordinates(innerCoords);
                });
              }
            }
            return count;
          };

          try {
            // Process all coordinates to find highest point and count points in one pass
            totalPoints = processCoordinates(Array.isArray(latLngs) && latLngs.length > 1 ? latLngs[0] : latLngs);

            // Fallback to bounds if we couldn't find any valid points
            if (!validPointsFound) {
              console.log(`No valid points found for polygon zone=${zone}, falling back to bounds.getNorth()`);
              highestLat = bounds.getNorth();
              highestLong = bounds.getWest();
            }
            // Use different positioning logic based on shape complexity
            const tooltipPosition = window.L.latLng(
              highestLat, // Use the actual northmost latitude
              totalPoints === 4 ? center.lng : highestLong // For rectangles, center horizontally; for other shapes, use the longitude of the highest point
            );

            // Bind tooltip with options
            polygon.bindTooltip(tooltipContent, tooltipOptions).setTooltipContent(tooltipContent);

            // Position the tooltip at the northmost point
            const tooltip = polygon?.getTooltip();
            if (tooltip) {
              tooltip.setLatLng(tooltipPosition);
            }
          } catch (err) {
            console.error("Error positioning tooltip:", err);
            // Fallback to standard tooltip binding in case of error
            polygon.bindTooltip(tooltipContent, tooltipOptions);
          }
        } else {
          // For innermost shape, use standard binding
          polygon.bindTooltip(tooltipContent, tooltipOptions);
        }
      });
    },

    /**
     * Determines if a polygon is an inner shape by checking containment
     * @param {L.Polygon} polygon - The polygon to check
     * @param {Array} otherShapes - Array of other shapes to compare against
     * @returns {Boolean} True if the shape is contained within another shape
     */
    isInnerShape(polygon, otherShapes) {
      if (!polygon || !otherShapes || otherShapes.length < 2) {
        return false;
      }

      // Get center point of this polygon
      const center = polygon.getBounds().getCenter();

      // Check if this polygon's center is contained within any other polygon
      for (let i = 0; i < otherShapes.length; i++) {
        const otherPolygon = otherShapes[i]?.polygon;
        if (otherPolygon && otherPolygon !== polygon) {
          // Use leaflet's built-in contains method if available
          if (typeof otherPolygon.contains === 'function' && otherPolygon.contains(center)) {
            return true;
          }

          // Fallback: check if the other polygon's bounds fully contain this polygon's bounds
          const thisBounds = polygon.getBounds();
          const otherBounds = otherPolygon.getBounds();

          if (otherBounds.contains(thisBounds)) {
            // Further verification: check the area difference
            const thisArea = this.calculatePolygonArea(polygon);
            const otherArea = this.calculatePolygonArea(otherPolygon);

            // If this polygon is significantly smaller, it's likely an inner shape
            if (thisArea < otherArea * 0.8) {
              return true;
            }
          }
        }
      }

      return false;
    },
    /**
     * To render all of the zones to the map
     * Usually called after editing or writing on the Zone database
     */
    async renderZonesToMap(isGettingZone, region) {
      if (!region || region === "ALL REGIONS") {
        this.drawnZones.forEach((d) => {
          this.map.removeLayer(this.map._layers[d.leafletId]);
        });
        this.drawnZones = [];
        return;
      }
      // this is to render all zones to the map
      const province = region;
      const friendlyName = this.regions.find((r) => r.id === province)
        ? this.regions.find((r) => r.id === province).name
        : "";
      if (isGettingZone) {
        this.$notify({
          group: "zones",
          title: "Getting Zones",
          closeOnClick: false,
          duration: -1,
          text: `Retrieving zones for ${friendlyName}, please wait...`,
        });
      } else {
        this.$notify({
          group: "zones",
          clean: true,
        });
        this.$notify({
          group: "zones",
          title: "Processing",
          closeOnClick: false,
          duration: -1,
          text: "Data processing, please wait...",
        });
      }
      try {
        this.showChangingRegionModal = true;
        const res = await getZones(province);
        const drawnShapes = res.map((doc) => ({ id: doc._id, ...doc }));

        // Filter shapes by type if a filter is applied
        const filteredShapes = this.selectedShapeType !== "ALL_TYPES"
          ? drawnShapes.filter(shape => {
            // First filter by type
            if (shape.type !== this.selectedShapeType) {
              return false;
            }

            // Then filter by clients if applicable
            if ((this.selectedShapeType === 'billable' || this.selectedShapeType === 'payable') &&
              this.selectedClients &&
              this.selectedClients.length > 0 &&
              !this.selectedClients.includes('ALL_CLIENTS')) {
              // Check if shape has clients and if any of the selected clients match
              if (!shape.clients || !Array.isArray(shape.clients) || shape.clients.length === 0) {
                return false;
              }

              // Check if any of the selected clients are in the shape's clients array
              const hasMatchingClient = this.selectedClients.some(selectedClient =>
                shape.clients.includes(selectedClient)
              );

              return hasMatchingClient;
            }

            return true;
          })
          : drawnShapes;

        this.$store.dispatch("setRegionZones", drawnShapes);
        this.zones = filteredShapes; // Use filtered shapes instead of all shapes
        this.drawnZones.forEach((d) => {
          this.map.removeLayer(this.map._layers[d.leafletId]);
        });
        this.markerGroup.clearLayers();
        this.drawnZones = [];
        for (const x in this.drawnItems._layers) {
          this.drawnItems.removeLayer(this.drawnItems._layers[x]);
        }

        const allPolygons = [];
        // FIRST STEP: Create all polygons without tooltips
        filteredShapes.map((shape, index) => {
          const coordinatesForLeaflet = shape.multi
            ? JSON.parse(shape.coordinates).map((shape) => {
              return shape.map((c) => [c[1], c[0]]);
            })
            : JSON.parse(shape.coordinates).map((c) => [c[1], c[0]]);
          const invertedOrderCoordinates = JSON.parse(shape.coordinates);
          // Determine color based on shape type
          let color;
          if (shape.type === 'geofence') {
            // For geofence shapes, use the shape's color
            color = shape.color || '#ff0000';
          } else if (shape.type === 'distance') {
            // For distance shapes, use the shape's color
            color = shape.color || '#3273dc';
          } else {
            // For billable and payable shapes, use the zone's color
            color = shape.zoneColor || '#3273dc';
          }
          const zone = shape.zone;
          const hubId = shape.distributionZoneId;
          const hub = this.hubs.find((hub) => hub.DistributionZoneId === hubId);

          // Create the polygon
          const polygon = window.L.polygon(coordinatesForLeaflet, {
            color: color,
            fillOpacity: 0.3,
            weight: 0.5,
          }).addTo(this.map);

          // Create instance for editing
          const instance = EditShape(
            this.$store,
            invertedOrderCoordinates,
            shape,
            this.$store.state.selectedVehicles,
            this.map,
            true /* editable */,
            this.editCurrentShapeModeOn,
            this.renderZonesToMap,
            this.activateEditSelectableFSAMode,
            this.closeEditCurrentShapeMode
          );
          instance.$mount();
          const contentPopup = instance.$el;

          const popupContent = window.L.popup({
            autoPan: false,
            closeButton: false,
            autoClose: false,
            maxWidth: 400,
            className: 'edit-shape-modal-popup' // Specific class for EditShapeModal
          }).setContent(contentPopup);
          polygon.bindPopup(popupContent);
          this.drawnItems.addLayer(polygon);

          allPolygons.push({
            polygon,
            zone,
            hub,
            index
          })
          // Store zone properties
          const zoneProperties = {
            zone,
            leafletId: polygon._leaflet_id,
            coordinates: coordinatesForLeaflet,
            id: shape.id,
            shapeId: shape.shapeId,
            zoneId: shape.zoneId,
            region: shape.region,
            multi: shape.multi,
            invertedCoordinates: invertedOrderCoordinates,
            daysAssigned: shape.daysAssigned,
            hub: hub,
            fsaIDs: shape.fsa
          };
          this.drawnZones.push(zoneProperties);
        });

        // SECOND STEP: Now add tooltips after all polygons are created
        this.addTooltipsToPolygons(allPolygons);

        this.$notify({
          group: "zones",
          clean: true,
        });
        this.$notify({
          type: "success",
          group: "zones",
          title: isGettingZone ? "Zones were retrieved" : "Zones were fetched",
          text: this.selectedShapeType !== "ALL_TYPES"
            ? `Map is updated - showing ${filteredShapes.length} ${this.selectedShapeType} shapes`
            : `Map is updated - showing ${filteredShapes.length} shapes`,
          duration: 3000,
        });
        this.showChangingRegionModal = false;
      } catch (err) {
        console.error("Error rendering zones to map:", err);
        if (err.name !== "TypeError") {
          this.$notify({
            group: "zones",
            clean: true,
          });
          this.$notify({
            type: "error",
            group: "zones",
            title: "There was an error, please refresh the page again",
          });
        }
      }
    },
    /**
     * To render all of the FSA to the map
     * Automatically the app will be on add FSA mode
     */

    async renderFSAToMap(province) {
      this.drawnFSA = [];
      const FSALayerGroup = window.L.featureGroup();
      const FSANameLayerGroup = window.L.featureGroup();
      this.FSALayerGroup = FSALayerGroup;
      this.FSANameLayerGroup = FSANameLayerGroup;
      this.retrievingFSA = true;
      this.$notify({
        group: "fsa",
        title: "Getting FSA",
        closeOnClick: false,
        duration: -1,
        text: "Retrieving FSA, please wait...",
      });
      const res = await getFSA(province);
      try {
        const fsaLocations = res;
        this.markerGroupFSA.clearLayers();
        fsaLocations.map((shape) => {
          const coordinatesForLeaflet = shape.isMulti
            ? JSON.parse(shape.coordinates).map((shape) => {
              return shape.map((c) => [c[1], c[0]]);
            })
            : JSON.parse(shape.coordinates).map((c) => [c[1], c[0]]);

          const invertedCoordinates = JSON.parse(shape.coordinates);
          const FSA = shape.FSA;
          const FSA_ID = shape.FSA_ID;

          const polygon = window.L.polygon(coordinatesForLeaflet, {
            color: "black",
            weight: 1,
            fill: true,
            fillColor: "#A2D26C",
            fillOpacity: 0.4,
          }).addTo(FSALayerGroup);

          const FSAProperties = {
            FSA,
            FSA_ID,
            coordinates: coordinatesForLeaflet,
            invertedCoordinates,
            id: polygon._leaflet_id,
          };
          this.drawnFSA.push(FSAProperties);
          // make fsa clickbale
          polygon.on("click", async (e) => {
            const selectedFSAId = e.sourceTarget._leaflet_id;
            const selectedFSAShape = this.drawnFSA.find(
              (d) => d.id === selectedFSAId
            );
            if (this.selectableFSAMode) {
              const selectedFSAShapeToAdd = this.selectedFSAToAdd.find(
                (d) => d.id === selectedFSAId
              );
              if (!selectedFSAShapeToAdd) {
                workerSelectFSA.postMessage({
                  drawnZones: this.drawnZones,
                  editSelectableFSAMode: this.editSelectableFSAMode,
                  selectedShapeID: this.selectedShape && this.selectedShape.id,
                  selectedFSAShape,
                  overlapEnabled: this.selectedShapeType !== "ALL_TYPES" && this.selectedShapeType !== "geofence",
                });
              } else {
                this.selectedFSAToAdd = this.selectedFSAToAdd.filter(
                  (d) => d.id !== selectedFSAId
                );
                await this.renderSelectedFSAShape();
              }
            }
          });
          const polygonFSA = polygonTurf(
            shape.isMulti ? coordinatesForLeaflet : [coordinatesForLeaflet]
          );
          const {
            geometry: { coordinates: centerPolygon },
          } = centerMass(polygonFSA);
          const tooltip = window.L.tooltip({
            permanent: true,
            sticky: true,
            direction: "center",
            opacity: "1",
            className: "FSA_tooltip",
            pane: "tooltipPane",
          });
          tooltip.setContent(`${FSA}`);
          tooltip.setLatLng(centerPolygon);
          this.markerGroupFSA.addLayer(tooltip);
        });
        const smoothLayer = window.L.tileLayer(this.urlSmooth);
        const outdoorsLayer = window.L.tileLayer(this.urlOutdoors);
        const urlOsrmBright = window.L.tileLayer(this.urlOsrmBright);
        const smoothDarkLayer = window.L.tileLayer(this.urlSmoothDark);

        // Base Layers (only one can be active)
        const baseLayers = {
          "Grey Map": smoothLayer,
          "Colored Map": outdoorsLayer,
          "Dark Map": smoothDarkLayer,
          "Bright Map": urlOsrmBright,
        };

        smoothLayer.addTo(this.map);

        // Overlay Layers (can be toggled independently)
        const overLayerMaps = {
          "FSA Layer": FSALayerGroup,
          "FSA Name": this.markerGroupFSA,
          Lanes: this.lanesLayerGroup,
          Stores: this.storeMarkers,
          Suppliers: this.supplierMarkers,
          Manufacturers: this.manufacturerMarkers,
          Primary: this.primaryMarkers,
          Secondary: this.secondaryMarkers,
          Stem: this.stemMarkers,
          "Center Zone": this.centerZoneMarkers,
        };
        // Create Layer Control with Base and Overlay Layers
        const layerControl = window.L.control.layers(
          baseLayers,
          overLayerMaps,
          {
            position: "topright",
          }
        );
        this.FSALayerControl = layerControl;
        window.FSALayerControl = layerControl;

        layerControl.addTo(this.map);
        this.storeMarkers.addTo(this.map);
        this.supplierMarkers.addTo(this.map);
        this.manufacturerMarkers.addTo(this.map);
        this.primaryMarkers.addTo(this.map);
        this.secondaryMarkers.addTo(this.map);
        this.stemMarkers.addTo(this.map);
        this.lanesLayerGroup.addTo(this.map);
        this.centerZoneMarkers.addTo(this.map);

        const fsaTargetElement = document.querySelector(
          ".leaflet-control-layers-overlays > :first-child"
        );
        const checkboxElement = fsaTargetElement.querySelector(
          ".leaflet-control-layers-selector"
        );
        const appThis = this;
        checkboxElement.addEventListener("change", async function () {
          if (this.checked) {
            await appThis.selectableFSAModeOn(true);
          } else {
            await appThis.closeSelectableFSAMode(false, true);
          }
        });
        this.$notify({
          group: "fsa",
          clean: true,
        });
        this.$notify({
          type: "success",
          group: "fsa",
          title: "FSA retrieved",
          text: `Fetched all FSA from ${province}`,
        });
        this.retrievingFSA = false;
        this.showChangingRegionModal = false;
        window.FSALayerGroup = this.FSALayerGroup;
      } catch (err) {
        this.$notify({
          group: "fsa",
          clean: true,
        });

        if (province && province !== "ALL REGIONS") {
          this.$notify({
            type: "error",
            group: "fsa",
            title: "FSA not retrieved",
            text: `${err}`,
          });
        }
        this.retrievingFSA = false;
        this.showChangingRegionModal = false;
      }
    },
    /**
     * Open Modal on FSA mode when adding new shape
     */
    openAddFSAShapeZoneModal() {
      // after selecting some fsa
      // this will checker of the formats of the shape before
      // show the modal to comfirm it
      workerCombineSelectedFSA.postMessage({
        selectedFSAToAdd: this.selectedFSAToAdd,
        drawnFSA: this.drawnFSA,
        mode: "ADD",
      });
    },
    /**
     * Open Modal on FSA mode when editing shape
     */
    openEditFSAShapeZoneModal() {
      // after selecting some fsa
      // this will checker of the formats of the shape before
      // show the modal to comfirm it
      workerCombineSelectedFSA.postMessage({
        selectedFSAToAdd: this.selectedFSAToAdd,
        drawnFSA: this.drawnFSA,
        mode: "EDIT",
      });
    },
    /**
     * Show FSA of the current region
     */
    selectableFSAModeOn(clickByCheckBox = false) {
      // this will activate selectable fsa mdoe and show fsa layer
      const fsaTargetElement = document.querySelector(
        ".leaflet-control-layers-overlays > :first-child"
      );
      const checkboxElement = fsaTargetElement.querySelector(
        ".leaflet-control-layers-selector"
      );
      this.map.removeControl(this.drawControl);
      this.map.closePopup();
      this.selectableFSAMode = true;
      // this is for activating fsa layer manually
      if (!clickByCheckBox) {
        checkboxElement.click();
        fsaTargetElement.style.pointerEvents = "none";
      }
    },
    /**
     * Activate FSA by selecting a shape then render current FSA assign on the current selected shape
     */
    async activateEditSelectableFSAMode() {
      // this will trigger to show all fsa to be clickable
      // this also will show all the fsa that the current shape overlaps
      this.map.removeControl(this.drawControl);
      this.map.closePopup();
      if (!this.selectableFSAMode) {
        const fsaTargetElement = document.querySelector(
          ".leaflet-control-layers-overlays > :first-child"
        );
        const checkboxElement = fsaTargetElement.querySelector(
          ".leaflet-control-layers-selector"
        );
        checkboxElement.click();
        fsaTargetElement.style.pointerEvents = "none";
      }
      this.selectableFSAMode = true;
      this.editSelectableFSAMode = true;
      const selectedShape = this.selectedShape;
      await this.drawnFSA.forEach(async (fsa) => {
        try {
          // fsaIDs // FSA_ID
          if (selectedShape.fsaIDs.includes(fsa.FSA_ID)) {
            this.selectedFSAToAdd.push(fsa);
            await this.renderSelectedFSAShape();
          }
        } catch (err) {
          console.error("err: ", err);
        }
      });
    },
    /**
     * Hide FSA layers
     */
    closeSelectableFSAMode(changeRegion = false, checkByCheckbox = false) {
      if (!changeRegion) {
        const fsaTargetElement = document.querySelector(
          ".leaflet-control-layers-overlays > :first-child"
        );
        const checkboxElement = fsaTargetElement.querySelector(
          ".leaflet-control-layers-selector"
        );
        fsaTargetElement.style.pointerEvents = "";
        if (!checkByCheckbox) {
          checkboxElement.click();
        }
      }
      if (
        this.toBeSaveShapeFSAID &&
        this.map._layers[this.toBeSaveShapeFSAID]
      ) {
        this.map.removeLayer(this.map._layers[this.toBeSaveShapeFSAID]);
        this.toBeSaveShapeFSAID = null;
      }
      if (this.currentSelectedFSAShape.length > 0) {
        this.currentSelectedFSAShape.forEach((fsa) => {
          if (this.map._layers[fsa.id]) {
            this.map.removeLayer(this.map._layers[fsa.id]);
          }
        });
      }
      this.selectedShape = null;
      this.selectedFSAToAdd = [];
      this.currentSelectedFSAShape = [];
      this.map.addControl(this.drawControl);
      this.selectableFSAMode = false;
      this.editSelectableFSAMode = false;
      this.FSAConfirmMode = false;
      this.onEditProcess = false;
    },
    /**
     * To render every FSA on click
     */
    renderSelectedFSAShape() {
      // console.log('RENDER THIS', this.selectedFSAToAdd)
      const wholeSelectedZone = this.selectedFSAToAdd.map((fsa) => ({
        coordinates: fsa.coordinates,
        fsaId: fsa.id,
      }));
      if (wholeSelectedZone.length > 0) {
        this.currentSelectedFSAShape.forEach((fsaProperty) => {
          this.map.removeLayer(this.map._layers[fsaProperty.id]);
        });
        this.currentSelectedFSAShape = [];
        wholeSelectedZone.map((fsa) => {
          const polygon = window.L.polygon([fsa.coordinates], {
            color: "black",
            weight: 1,
            fill: true,
            fillColor: "black",
            fillOpacity: 0.8,
            pane: "shadowPane",
          }).addTo(this.map);
          const selectedShapeProperty = {
            id: polygon._leaflet_id,
          };
          this.currentSelectedFSAShape.push(selectedShapeProperty);
          polygon.on("click", async (e) => {
            const selectedCurrentFSAId = e.sourceTarget._leaflet_id;
            this.map.removeLayer(this.map._layers[selectedCurrentFSAId]);
            this.currentSelectedFSAShape = this.currentSelectedFSAShape.filter(
              (d) => d.id !== selectedCurrentFSAId
            );
            this.selectedFSAToAdd = this.selectedFSAToAdd.filter(
              (d) => d.id !== fsa.fsaId
            );
          });
        });
      }
    },
    /**
     * Confirming shape after you the select it and show modal to choose what zone to assign
     */
    async renderFSAZoneToAddModal(
      invertedUnionShapeCoordinates,
      shapesToAdd,
      shapeType
    ) {
      // this is the confirmation after adding some selected fsa and
      // display the modal
      const fsaTargetElement = document.querySelector(
        ".leaflet-control-layers-overlays > :first-child"
      );
      // const checkboxElement = fsaTargetElement.querySelector(
      //   '.leaflet-control-layers-selector'
      // )
      fsaTargetElement.style.pointerEvents = "none";
      this.FSAConfirmMode = true;
      this.selectableFSAMode = false;
      this.currentSelectedFSAShape.forEach((fsaProperty) => {
        this.map.removeLayer(this.map._layers[fsaProperty.id]);
      });
      const polygon = window.L.polygon(invertedUnionShapeCoordinates, {
        color: "black",
        weight: 1,
        fill: true,
        fillColor: "black",
        fillOpacity: 0.8,
        // pane: 'tilePane'
      });
      // .addTo(this.map)
      // console.log('invertedCenterCoordinate: ', invertedCenterCoordinate)
      this.drawnItems.addLayer(polygon);
      const ComponentClass = Vue.extend(AddFSAZoneModal);
      const instance = new ComponentClass({
        propsData: {
          shapesToAdd,
          region: this.$store.state.region,
          shapeType,
          selectedShapeTypeFilter: this.selectedShapeType,
        },
        methods: {
          close: () => {
            this.map.closePopup();
          },
          updateMap: async (data) => {
            const { shapesToAdd, zoneId, shapeType, distributionZoneId, zoneClients, selectedType, daysOfService, color } = data;

            // For geofence type, use empty string for zoneId (shapeId)
            const shapesID = selectedType === 'geofence' ? "" : zoneId;
            if (shapeType === "SINGLE_SHAPE") {
              const coordinates = shapesToAdd.coordinates;
              const fsa = shapesToAdd.fsa;
              this.pushToQueue("INSERT_NEW_SHAPE", {
                coordinates: coordinates[0],
                shapesID: shapesID,
                multi: false,
                fsa,
                distributionZoneId,
                zoneClients,
                type: selectedType,
                daysOfService,
                color
              });
            } else if (shapeType === "SINGLE_SHAPE_WITH_HOLES") {
              const coordinates = shapesToAdd.coordinates;
              const fsa = shapesToAdd.fsa;
              this.pushToQueue("INSERT_NEW_SHAPE", {
                coordinates: coordinates,
                shapesID: shapesID,
                multi: true,
                fsa,
                distributionZoneId,
                zoneClients,
                type: selectedType,
                daysOfService,
                color
              });
            } else if (shapeType === "MULTI_SINGLE_SHAPE") {
              await Promise.all(
                shapesToAdd.map(async (shape) => {
                  const coordinates = shape.coordinates;
                  const fsa = shape.fsa;
                  this.pushToQueue("INSERT_NEW_SHAPE", {
                    coordinates: coordinates,
                    shapesID: shapesID,
                    multi: false,
                    fsa,
                    distributionZoneId,
                    zoneClients,
                    type: selectedType,
                    daysOfService,
                    color
                  });
                })
              );
            } else {
              await Promise.all(
                shapesToAdd.map(async (shape) => {
                  const coordinates = shape.coordinates;
                  const fsa = shape.fsa;
                  if (coordinates.length > 1) {
                    this.pushToQueue("INSERT_NEW_SHAPE", {
                      coordinates: coordinates,
                      shapesID: shapesID,
                      multi: true,
                      fsa,
                      distributionZoneId,
                      zoneClients,
                      type: selectedType,
                      daysOfService,
                      color
                    });
                  } else {
                    this.pushToQueue("INSERT_NEW_SHAPE", {
                      coordinates: coordinates[0],
                      shapesID: shapesID,
                      multi: false,
                      fsa,
                      distributionZoneId,
                      zoneClients,
                      type: selectedType,
                      daysOfService,
                      color
                    });
                  }
                })
              );
            }
          },
          removeShape: async () => {
            this.toBeSaveShapeFSAID = null;
            if (this.map._layers[polygon._leaflet_id]) {
              this.map.removeLayer(this.map._layers[polygon._leaflet_id]);
            }
            await this.closeSelectableFSAMode();
          },
          cancelEdit: async () => {
            await this.closeSelectableFSAMode();
          },
        },
      });
      instance.$mount();
      const contentPopup = instance.$el;
      const popupContent = window.L.popup({
        autoPan: false,
        closeButton: false,
        autoClose: false,
        closeOnClick: false,
        maxWidth: 400,
        className: 'add-fsa-zone-modal-popup' // Use add-fsa-zone-modal-popup class for AddFSAZoneModal
      }).setContent(contentPopup);
      polygon.bindPopup(popupContent).openPopup();
      this.toBeSaveShapeFSAID = polygon._leaflet_id;
    },
    /**
     * Confirming shape after adding to show modal what zone to assign
     */
    async renderNewZoneToAddModal(
      invertedUnionShapeCoordinates,
      shapesToAdd,
      shapeType
    ) {
      const polygon = window.L.polygon(invertedUnionShapeCoordinates, {
        color: "black",
        weight: 1,
        fill: true,
        fillColor: "black",
        fillOpacity: 0.8,
      });
      this.drawnItems.addLayer(polygon);
      const ComponentClass = Vue.extend(AddFSAZoneModal);
      const instance = new ComponentClass({
        propsData: {
          shapesToAdd,
          region: this.$store.state.region,
          shapeType,
          selectedShapeTypeFilter: this.selectedShapeType,
        },
        methods: {
          close: () => {
            this.$notify({
              group: "zones",
              clean: true,
            });
            this.map.closePopup();
            if (this.map._layers[polygon._leaflet_id]) {
              this.map.removeLayer(polygon);
              this.drawnItems.removeLayer(polygon);
            }
            // this.renderZonesToMap(false, this.$store.state.region);
          },
          updateMap: async (data) => {
            const { shapesToAdd, zoneId, shapeType, distributionZoneId, zoneClients, selectedType, daysOfService, color } = data;

            // For geofence type, use empty string for zoneId (shapeId)
            const shapesID = selectedType === 'geofence' ? "" : zoneId;
            if (shapeType === "SINGLE_SHAPE") {
              const coordinates = shapesToAdd.coordinates;
              const fsa = shapesToAdd.fsa;
              this.pushToQueue("INSERT_NEW_SHAPE", {
                coordinates: coordinates[0],
                shapesID: shapesID,
                multi: false,
                fsa,
                distributionZoneId,
                zoneClients,
                type: selectedType,
                daysOfService,
                color
              });
            } else if (shapeType === "SINGLE_SHAPE_WITH_HOLES") {
              const coordinates = shapesToAdd.coordinates;
              const fsa = shapesToAdd.fsa;
              this.pushToQueue("INSERT_NEW_SHAPE", {
                coordinates: coordinates,
                shapesID: shapesID,
                multi: true,
                fsa,
                distributionZoneId,
                zoneClients,
                type: selectedType,
                daysOfService,
                color
              });
            } else if (shapeType === "MULTI_SINGLE_SHAPE") {
              await Promise.all(
                shapesToAdd.map(async (shape) => {
                  const coordinates = shape.coordinates;
                  const fsa = shape.fsa;
                  this.pushToQueue("INSERT_NEW_SHAPE", {
                    coordinates: coordinates,
                    shapesID: shapesID,
                    multi: false,
                    fsa,
                    distributionZoneId,
                    zoneClients,
                    type: selectedType,
                    daysOfService,
                    color
                  });
                })
              );
            } else {
              await Promise.all(
                shapesToAdd.map(async (shape) => {
                  const coordinates = shape.coordinates;
                  const fsa = shape.fsa;
                  if (coordinates.length > 1) {
                    this.pushToQueue("INSERT_NEW_SHAPE", {
                      coordinates: coordinates,
                      shapesID: shapesID,
                      multi: true,
                      fsa,
                      distributionZoneId,
                      zoneClients,
                      type: selectedType,
                      daysOfService,
                      color
                    });
                  } else {
                    this.pushToQueue("INSERT_NEW_SHAPE", {
                      coordinates: coordinates[0],
                      shapesID: shapesID,
                      multi: false,
                      fsa,
                      distributionZoneId,
                      zoneClients,
                      type: selectedType,
                      daysOfService,
                      color
                    });
                  }
                })
              );
            }
          },
          removeShape: async () => {
            if (this.map._layers[polygon._leaflet_id]) {
              this.map.removeLayer(this.map._layers[polygon._leaflet_id]);
            }
          },
          cancelEdit: async () => {
            this.map.closePopup();
            if (this.map._layers[polygon._leaflet_id]) {
              this.map.removeLayer(this.map._layers[polygon._leaflet_id]);
            }
          },
        },
      });
      instance.$mount();
      const contentPopup = instance.$el;
      const popupContent = window.L.popup({
        autoPan: false,
        closeButton: false,
        autoClose: false,
        closeOnClick: false,
        maxWidth: 400,
        className: 'add-fsa-zone-modal-popup' // Use add-fsa-zone-modal-popup class for AddFSAZoneModal
      }).setContent(contentPopup);
      polygon.bindPopup(popupContent).openPopup();
    },
    /**
     * Confirming shape after you the select it and show modal to choose what zone to assign
     */
    async renderFSAZoneToEditModal(
      invertedUnionShapeCoordinates,
      shapesToAdd,
      shapeType
    ) {
      // this is the confirmation after adding some selected fsa and
      // display the modal
      const fsaTargetElement = document.querySelector(
        ".leaflet-control-layers-overlays > :first-child"
      );
      // const checkboxElement = fsaTargetElement.querySelector(
      //   '.leaflet-control-layers-selector'
      // )
      fsaTargetElement.style.pointerEvents = "none";
      this.FSAConfirmMode = true;
      this.selectableFSAMode = false;
      const selectedShape = this.selectedShape;
      const polygon = window.L.polygon(invertedUnionShapeCoordinates, {
        color: "black",
        weight: 1,
        fill: true,
        fillColor: "black",
        fillOpacity: 0.8,
        // pane: 'tilePane'
      });
      this.drawnItems.addLayer(polygon);
      const ComponentClass = Vue.extend(EditFSAZoneModal);
      const instance = new ComponentClass({
        propsData: {
          shapesToAdd,
          shapeType,
          shape: selectedShape,
          editable: true,
        },
        methods: {
          close: () => {
            this.map.closePopup();
          },
          updateZone: async (state) => {
            const { coordinates, shapeId, multi, fsa } = state;
            this.onEditProcess = true;
            await this.pushToQueue("EDIT_COORDINATES", {
              coordinates,
              shapeId,
              multi,
              fsa,
            });
          },
          updateMultipleZone: async (state) => {
            const { newShapes, currentShape, shapeType } = state;
            this.onEditProcess = true;
            this.pushToQueue("EDIT_MULTIPLE_ZONE_FSA", {
              newShapes,
              currentShape,
              shapeType,
            });
          },
          activateEditOnProcess: () => {
            this.onEditProcess = true;
          },
          updateMap: async () => {
            this.$notify({
              group: "zones",
              clean: true,
            });
          },
          removeShape: async () => {
            this.toBeSaveShapeFSAID = null;
            if (this.map._layers[polygon._leaflet_id]) {
              this.map.removeLayer(this.map._layers[polygon._leaflet_id]);
            }
            await this.closeSelectableFSAMode();
          },
          cancelEdit: async () => {
            await this.closeSelectableFSAMode();
          },
        },
      });
      // .addTo(this.map)
      instance.$mount();
      const contentPopup = instance.$el;
      const popupContent = window.L.popup({
        autoPan: false,
        closeButton: false,
        autoClose: false,
        closeOnClick: false,
        maxWidth: 400,
        className: 'add-fsa-zone-modal-popup' // Use add-fsa-zone-modal-popup class for AddFSAZoneModal
      }).setContent(contentPopup);
      polygon.bindPopup(popupContent).openPopup();
      this.toBeSaveShapeFSAID = polygon._leaflet_id;
    },
    async refresh() {
      this.refreshLoading = true;
      // Reset filter when refreshing
      this.selectedShapeType = "ALL_TYPES";
      this.selectedClients = [];
      await this.renderZonesToMap(false, this.$store.state.region);
      this.refreshLoading = false;
    },
    /**
     * Calculate the approximate area of a polygon
     */
    calculatePolygonArea(coords) {
      try {
        // Simple area calculation for approximation
        let area = 0;
        if (!coords || coords.length < 3) return 0;

        // Handle multi-polygon case
        if (Array.isArray(coords[0]) && Array.isArray(coords[0][0])) {
          // This is a multi-polygon, take the first polygon
          coords = coords[0];
        }

        // Calculate area using shoelace formula
        for (let i = 0; i < coords.length; i++) {
          const j = (i + 1) % coords.length;
          area += coords[i][0] * coords[j][1];
          area -= coords[j][0] * coords[i][1];
        }

        area = Math.abs(area) / 2;
        return area;
      } catch (err) {
        console.error("Error calculating polygon area:", err);
        return 0;
      }
    },
    errorNotificatation() {
      this.$notify({
        group: "zones",
        clean: true,
      });
      this.$notify({
        type: "error",
        group: "zones",
        title: "Shape is not saved",
        text: "The shape is not a valid shape",
      });
      this.closeAddDeletePartShapeMode();
    },
    /* Push any action to the queue to be processed */
    async pushToQueue(action, state) {
      return this.queue.push({
        action,
        state,
      });
    },
    removeActionInQueue(id) {
      this.queue.splice(id, 1);
    },
    logout() {
      this.$store.dispatch("resetValues");
      localStorage.removeItem("user");
      localStorage.removeItem("access");
      localStorage.removeItem("environment");
      auth.signOut();
      this.$router.push("/");
    },
    processLanesWithHubs(newVal) {
      // This method returns an array of lane objects with their associated hub data
      return (newVal || []).map(lane => {
        // Find all the hubs for this lane, excluding CENTER ZONE hubs
        const laneHubs = lane.hubs.map(hubId => {
          const hub = this.hubs.find(h => h.DistributionZoneId === hubId);
          if (!hub) {
            console.warn(`Hub with ID ${hubId} not found for lane ${lane.id}`);
            return null;
          }

          // Filter out CENTER ZONE hubs from lane creation
          const distributionZoneType = this.$store.getters.DistributionZoneTypes.find(
            (type) => type.value === hub.DistributionZoneType
          );
          if (distributionZoneType?.label === "CENTER ZONE") {
            console.warn(`CENTER ZONE hub ${hub.DistributionZoneName} excluded from lane ${lane.id}`);
            return null;
          }

          return {
            id: hubId,
            name: hub.DistributionZoneName,
            coordinates: {
              lat: hub.Center.latitude,
              lng: hub.Center.longitude
            }
          };
        }).filter(hub => hub !== null); // Remove any null entries

        // Only include lanes with at least 2 hubs
        if (laneHubs.length >= 2) {
          return {
            id: lane.id,
            data: laneHubs
          };
        }

        return null;
      }).filter(lane => lane !== null); // Remove any null entries
    },
    handleLaneSaved() {
      this.showLaneWizard = false;
      this.laneWizardHubIds = [];
      this.laneWizardData = null;
      // No need to manually fetch lanes as they are updated via the onSnapshot listener
    },
    async handleHubsChange(region) {
      // TODO: update the markers when it has update, shouldn't reset all markers everytime.
      const drawnMarkers = new window.L.FeatureGroup();
      // Filter hubs for the current region from the global hubs state
      const regionHubs = region === "ALL REGIONS" ? this.hubs : this.hubs.filter(hub => hub.Region === `${region}`);
      const drawnHubs = regionHubs?.map((hub) => ({
        id: hub.id,
        ...hub,
      }));
      this.drawnMarkers
        .map((d) => d.leafletId)
        .forEach((id) => {
          if (this.map._layers[id]) {
            this.map.removeLayer(this.map._layers[id]);
          }
        });

      this.drawnMarkers = [];

      for (const x in drawnMarkers._layers) {
        if (x) {
          drawnMarkers.removeLayer(drawnMarkers._layers[x]);
        }
      }

      // If we're in custom polyline mode, exit it to avoid issues with marker handlers
      if (this.isInCustomPolylineMode) {
        this.cancelCustomPolylineDrawing();
      }
      drawnHubs.map((DistributionZone) => {
        const coordinates = {
          lat: DistributionZone.Center.latitude,
          lng: DistributionZone.Center.longitude,
        };
        const color = DistributionZone.Color;
        /* 0, 1, 2: MAIN, SUB, STORE */
        console.log("store", this.$store.getters.DistributionZoneTypes);
        const distributionZoneType =
          this.$store.getters.DistributionZoneTypes.find(
            (type) =>
              type.value === DistributionZone.DistributionZoneType
          ) || null;
        const DistributionZoneId = DistributionZone.DistributionZoneId;

        let timeZone = DistributionZone.TimeZone || "";
        const gjLayer = window.L.geoJson(JSON.parse(geoJsonFeature));

        if (!timeZone) {
          const firstPointResult = leafletPip.pointInLayer(
            [coordinates.lng, coordinates.lat],
            gjLayer
          );
          timeZone =
            firstPointResult[0].feature.properties.tz_name1st || "";
        }

        const marker = new window.L.Marker(coordinates, {
          icon: this.getIcon(color, distributionZoneType?.label),
          riseOnHover: true,
          draggable: true,
        }).addTo(this.map);

        // Add tooltip to show hub name on hover
        if (DistributionZone?.DistributionZoneName) {
          marker.bindTooltip(DistributionZone.DistributionZoneName, {
            permanent: false,
            direction: 'top',
            className: 'hub-tooltip'
          });
        }
        console.log("distributionZoneType", distributionZoneType);
        this.addMarkerToLayerGroup(marker, distributionZoneType.label);
        //   const zone = shape.zone
        const ComponentClass = Vue.extend(EditMarkerZoneModal);
        const instance = new ComponentClass({
          propsData: {
            store: this.$store,
            map: this.map,
            DistributionZone: JSON.parse(
              JSON.stringify(DistributionZone)
            ),
            editable: true,
            coordinates: coordinates,
            region: this.$store.state.region,
            timeZone: timeZone,
          },
          methods: {
            remove: () => {
              if (this.map._layers[marker._leaflet_id]) {
                this.map.removeLayer(
                  this.map._layers[marker._leaflet_id]
                );
              }
              this.map.closePopup();
            },
            close: () => {
              marker.closePopup();
            },
          },
        });
        instance.$mount();

        // Listen for type changes to update marker icon
        instance.$on("type-change", ({ type, color }) => {
          if (marker && marker.setIcon) {
            const newIcon = this.getIcon(color, type);
            marker.setIcon(newIcon);
          }
        });

        const contentPopup = window.L.popup({
          className: "hub-content-popup",
          closeButton: true,
          autoPan: true,
        })

        contentPopup.setContent(instance.$el);

        const markerProperties = {
          leafletId: marker._leaflet_id,
          coordinates,
          id: DistributionZoneId,
          region: DistributionZone.regionHub,
          timeZone: timeZone,
        };

        this.drawnMarkers.push(markerProperties);
        marker.bindPopup(contentPopup);

        this.setMarkerEventListener(marker, instance, gjLayer, false);

        drawnMarkers.addLayer(marker);
        // add marker to oms instance
        this.spiderfy.addMarker(marker);
      });
    },
    updateFsaLayerVisibility(selectedRegion) {
      // Check if the selected region is 'ALL REGIONS'
      const showFsaLayers = selectedRegion !== 'ALL REGIONS';
      if (showFsaLayers) {
        this.enableLayerCheckbox('FSA Layer');
        this.enableLayerCheckbox('FSA Name');
      } else {
        this.disableLayerCheckbox('FSA Layer');
        this.disableLayerCheckbox('FSA Name');
      }
    },
    disableLayerCheckbox(layerName) {
      // Find the layer control container
      const container = this.FSALayerControl._container;

      // Find all labels in the overlay section
      const labels = container?.querySelectorAll('.leaflet-control-layers-overlays label');

      // Find the one with our layer name
      for (const label of labels || []) {
        if (label.textContent.includes(layerName)) {
          // Get the checkbox
          const checkbox = label.querySelector('input');
          if (checkbox) checkbox.disabled = true;
          // Optionally add a visual indication
          label.style.display = 'none';
          break;
        }
      }
    },

    enableLayerCheckbox(layerName) {
      // Similar to above but enable the checkbox
      const container = this.FSALayerControl?._container;
      const labels = container?.querySelectorAll('.leaflet-control-layers-overlays label');

      for (const label of labels || []) {
        if (label.textContent.includes(layerName)) {
          const checkbox = label.querySelector('input');
          if (checkbox) checkbox.disabled = false;
          label.style.display = 'block';
          break;
        }
      }
    },
    /**
     * Updates the visibility of the drawControl based on the selectedRegion and shape type
     * Hides the control when selectedRegion is null, 'ALL REGIONS', or when shape type is 'distance'
     */
    updateDrawControlVisibility(selectedRegion) {
      if (this.map && this.drawControl) {
        try {
          // Hide control when region is 'ALL REGIONS' or null
          const shouldHide = selectedRegion === "ALL REGIONS" ||
            selectedRegion === null;
          console.log("shouldHide", shouldHide, this.selectedShapeType);

          if (shouldHide) {
            // Check if control is already on the map before removing
            if (document.querySelector(".leaflet-draw")) {
              this.map.removeControl(this.drawControl);
            }
          } else {
            // Check if control is not on the map before adding
            if (!document.querySelector(".leaflet-draw")) {
              this.map.addControl(this.drawControl);
            }
          }
        } catch (error) {
          console.error("Error updating draw control visibility:", error);
        }
      }
    },
    // Add detection method to check for potential overlap
    /**
     * Detect if a polygon potentially overlaps with other polygons
     * @param {L.Polygon} polygon - The polygon to check
     * @param {Array} allShapes - All shapes on the map
     * @returns {Boolean} - True if potential overlap detected
     */
    detectPotentialOverlap(polygon, restShapes) {
      if (!polygon || !restShapes || restShapes.length < 2) {
        return false;
      }

      const bounds = polygon.getBounds();
      let overlappingCount = 0;

      // Check how many other shapes' bounds intersect with this one
      for (let i = 0; i < restShapes.length; i++) {
        const otherPolygon = restShapes[i]?.polygon;
        if (otherPolygon && otherPolygon !== polygon) {
          const otherBounds = otherPolygon.getBounds();
          if (bounds.intersects(otherBounds)) {
            overlappingCount++;
            if (overlappingCount > 0) {
              return true; // Found at least one overlap
            }
          }
        }
      }

      return false;
    },
    /**
     * Groups polygons into distinct nested groups
     * @param {Array} polygonsWithArea - Array of polygons with calculated areas
     * @returns {Array} Array of polygon groups, each containing nested polygons
     */
    groupNestedPolygons(polygonsWithArea) {
      if (!polygonsWithArea?.length) return [];

      const groups = [];
      const processed = new Set();

      // Start with the smallest polygon and build groups
      for (const polygonData of polygonsWithArea) {
        if (processed.has(polygonData.polygon)) continue;

        // Start a new group with this polygon
        const group = [polygonData];
        processed.add(polygonData.polygon);

        // Find all polygons that contain this polygon
        for (const outerPolygon of polygonsWithArea) {
          if (processed.has(outerPolygon.polygon)) continue;
          if (outerPolygon.polygon === polygonData.polygon) continue;

          // Check if this polygon is contained within outerPolygon
          if (this.isPolygonContainedIn(polygonData.polygon, outerPolygon.polygon)) {
            group.push(outerPolygon);
            processed.add(outerPolygon.polygon);
          }
        }
        // Add group if it has at least one polygon
        if (group.length > 0) {
          // Sort by area ascending (smallest first)
          group.sort((a, b) => a.area - b.area);
          groups.push(group);
        }
      }

      return groups;
    },

    /**
     * Checks if a polygon is contained within another polygon
     * @param {L.Polygon} innerPolygon - The potentially inner polygon
     * @param {L.Polygon} outerPolygon - The potentially outer polygon
     * @returns {Boolean} True if innerPolygon is contained within outerPolygon
     */
    isPolygonContainedIn(innerPolygon, outerPolygon) {
      try {
        if (!innerPolygon || !outerPolygon) return false;

        // Get center point of inner polygon
        const center = innerPolygon.getBounds()?.getCenter();
        if (!center) return false;

        // Check if center is contained in outer polygon
        return outerPolygon.getBounds()?.contains(center) && outerPolygon?.getBounds()?.contains(center);
      } catch (error) {
        return false;
      }
    },

    /**
     * Finds which group a polygon belongs to and whether it's the innermost in that group
     * @param {L.Polygon} polygon - The polygon to check
     * @param {Array} groups - Array of polygon groups
     * @returns {Object} Object with group index and isInnermost flag
     */
    findPolygonGroupInfo(polygon, groups) {
      if (!polygon || !groups?.length) return { groupIndex: -1, isInnermost: false };

      for (let i = 0; i < groups.length; i++) {
        const group = groups[i];
        if (!group?.length) continue;

        // Find this polygon in the group
        const polygonIndex = group.findIndex(item => item.polygon === polygon);

        if (polygonIndex >= 0) {
          // It's the innermost if it's the first one in the group (smallest area)
          return {
            groupIndex: i,
            isInnermost: polygonIndex === 0
          };
        }
      }

      return { groupIndex: -1, isInnermost: false };
    },
    /**
     * Checks if a polygon has holes in it
     * @param {L.Polygon} polygon - The polygon to check
     * @returns {Boolean} True if the polygon has holes
     */
    polygonHasHoles(polygon) {
      if (!polygon) return false;

      // Get the Leaflet LatLngs array from the polygon
      const latlngs = polygon.getLatLngs();

      // In Leaflet, a polygon with holes will have nested arrays
      // The first array is the outer ring, subsequent arrays are holes
      return Array.isArray(latlngs) &&
        latlngs.length > 0 &&
        Array.isArray(latlngs[0]) &&
        latlngs.length > 1;
    },
    /**
     * Toggles the overlapping shapes option and shows notification
     */
    toggleOverlappingShapes() {
      console.log('toggleOverlappingShapes', this.selectedShapeType);

      if (this.selectedShapeType === "ALL_TYPES" || this.selectedShapeType === "geofence" || this.selectedShapeType === "distance") {
        return;
      }
      this.allowOverlappingShapes = !this.allowOverlappingShapes;
      const status = this.allowOverlappingShapes ? 'enabled' : 'disabled';

      // Update the icon button
      this.updateIconOverlappingButton();

      this.$notify({
        type: "info",
        group: "zones",
        title: "Overlapping Shapes",
        text: `Overlapping shapes ${status}. ${this.allowOverlappingShapes ? 'Shapes can overlap without cropping.' : 'Shapes will be cropped to avoid overlaps.'}`,
        duration: 3000,
      });
    },
    /**
     * Creates a custom draw control that includes the overlapping toggle
     */
    createCustomDrawControl() {
      const CustomDrawControl = window.L.Control.Draw.extend({
        options: {
          position: "topright",
        },

        onAdd: (map) => {
          // Disable drawing for distance mode since shapes are auto-generated
          const isDistanceMode = this.selectedShapeType === "distance";

          // Create the original draw control
          const originalControl = new window.L.Control.Draw({
            edit: {
              featureGroup: this.drawnItems,
              edit: false,
            },
            draw: {
              polygon: {
                shapeOptions: {
                  clickable: true,
                  color: "#00D1B2",
                },
                allowIntersection: this.allowOverlappingShapes,
              },
              rectangle: {
                shapeOptions: {
                  clickable: true,
                  color: "#00D1B2",
                }
              },
              marker: {
                shapeOptions: {
                  clickable: true,
                  color: "#6169e0",
                },
              },
              polyline: false,
              circlemarker: false,
            },
            position: "topright",
          });

          // If in distance mode, disable all drawing tools
          if (isDistanceMode) {
            originalControl.setDrawingOptions({
              polygon: false,
              rectangle: false,
              marker: false,
              polyline: false,
              circlemarker: false,
            });
          }

          // Get the original container
          const originalContainer = originalControl.onAdd(map);

          const toggleContainer = window.L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-icon-overlapping-control');
          const button = window.L.DomUtil.create('a', 'icon-overlapping-button', toggleContainer);

          button.href = '#';
          button.title = this.allowOverlappingShapes ? 'Disable overlapping shapes' : 'Enable overlapping shapes';

          // Create overlapping shapes icon using SVG
          const overlappingIcon = `<img src="${this.allowOverlappingShapes ? overlapESvg : overlapDSvg}" 
                                       alt="Overlapping shapes" 
                                       style="display: block; margin: 0 auto; width: 32px; height: 32px; padding: ${this.allowOverlappingShapes ? '2px' : '1px'}" />`;

          button.innerHTML = overlappingIcon;
          button.style.width = '44px';
          button.style.height = '44px';
          button.style.backgroundColor = this.allowOverlappingShapes ? '#007D6A' : '#fff';
          button.style.textAlign = 'center';
          button.style.fontSize = '16px';
          button.style.color = this.allowOverlappingShapes ? 'white' : 'black';
          button.style.display = 'block';
          button.style.borderRadius = '4px';
          button.style.transition = 'all 0.2s ease';

          window.L.DomEvent.on(button, 'click', window.L.DomEvent.stop);
          window.L.DomEvent.on(button, 'click', () => {
            this.toggleOverlappingShapes();
          });

          // Append the toggle container to the original container
          originalContainer.appendChild(toggleContainer);

          return originalContainer;
        }
      });

      return new CustomDrawControl();
    },

    /**
     * Updates the icon-based overlapping button appearance
     */
    updateIconOverlappingButton() {
      const allowOverlapping = !(this.selectedShapeType === "ALL_TYPES" || this.selectedShapeType === "geofence" || this.selectedShapeType === "distance") && this.allowOverlappingShapes;
      const button = document.querySelector('.icon-overlapping-button');
      if (button) {
        button.title = allowOverlapping ? 'Disable overlapping shapes' : 'Enable overlapping shapes';

        // Regenerate the entire SVG icon
        const overlappingIcon = `<img src="${allowOverlapping ? overlapESvg : overlapDSvg}" 
                                     alt="Overlapping shapes" 
                                     style="display: block; margin: 0 auto; width: 32px; height: 32px; padding: ${allowOverlapping ? '2px' : '1px'}" />`;
        button.innerHTML = overlappingIcon;
      }
    },
    /**
     * Recreates the drawing control based on the selected shape type
     * For distance type: shows only marker drawing tool
     * For other types: shows all drawing tools
     * @param {String} shapeType - The selected shape type
     */
    recreateDrawingControlForShapeType(shapeType) {
      if (!this.map || !this.drawControl) {
        return;
      }

      try {
        // Remove the existing control
        this.map.removeControl(this.drawControl);

        // Create a new control with the correct options based on shape type
        const isDistanceMode = shapeType === "distance";

        if (isDistanceMode) {
          // Create control with only marker tool for distance mode
          const distanceDrawControl = new window.L.Control.Draw({
            edit: {
              featureGroup: this.drawnItems,
              edit: false,
            },
            draw: {
              polygon: false,
              rectangle: false,
              circle: false,
              marker: {
                shapeOptions: {
                  clickable: true,
                  color: "#6169e0",
                },
              },
              polyline: false,
              circlemarker: false,
            },
            position: "topright",
          });

          this.drawControl = distanceDrawControl;
        } else {
          // Create control with all tools for other modes
          const customDrawControl = this.createCustomDrawControl();
          this.drawControl = customDrawControl;
        }

        // Add the new control to the map
        this.map.addControl(this.drawControl);

        console.log(`Drawing control recreated for shape type: ${shapeType}`);
      } catch (error) {
        console.error("Error recreating drawing control:", error);
      }
    },
    /**
     * Applies the shape type filter and re-renders the map
     */
    applyShapeTypeFilter() {
      console.log('Applying shape type filter:', this.selectedShapeType);

      // Validate the filter value
      const validTypes = ["ALL_TYPES", "geofence", "distance", "billable", "payable"];
      if (!validTypes.includes(this.selectedShapeType)) {
        this.$notify({
          type: "error",
          group: "zones",
          title: "Invalid Filter",
          text: "Please select a valid shape type filter",
          duration: 3000,
        });
        return;
      }

      // Check if map is ready before proceeding
      if (!this.isMapReady()) {
        return;
      }

      // Re-render zones to apply the filter
      if (this.$store.state.region && this.$store.state.region !== "ALL REGIONS") {
        this.renderZonesToMap(false, this.$store.state.region);
      }
      console.log("selectedShapeType", this.selectedShapeType);
      // Show notification about the filter
      if (this.selectedShapeType !== "ALL_TYPES") {
        const counts = this.getShapeTypeCounts();
        const filteredCount = counts[this.selectedShapeType] || 0;

        // Build notification message
        let notificationText = `Showing ${filteredCount} ${this.selectedShapeType} shapes out of ${counts.total} total shapes`;

        if (this.selectedShapeType === "ALL_TYPES" || this.selectedShapeType === "geofence" || this.selectedShapeType === "distance") {
          this.allowOverlappingShapes = false;
          this.updateIconOverlappingButton();
        }

        // Update draw control visibility based on shape type
        this.updateDrawControlVisibility(this.$store.state.region);

        // Show notification for distance mode
        if (this.selectedShapeType === "distance") {
          this.$notify({
            type: "info",
            group: "zones",
            title: "Distance Mode Active",
            text: "Only marker drawing is available. Distance shapes are automatically generated when creating zones.",
            duration: 4000,
          });
        }

        // Add client filtering info if applicable
        if ((this.selectedShapeType === 'billable' || this.selectedShapeType === 'payable') &&
          this.selectedClients &&
          this.selectedClients.length > 0 &&
          !this.selectedClients.includes('ALL_CLIENTS')) {
          const clientLabels = this.selectedClients.map(clientId => {
            const option = this.clientOptions.find(opt => opt.value === clientId);
            return option ? option.label : clientId;
          });

          notificationText += ` (filtered by ${clientLabels.join(', ')})`;
        }

        if (filteredCount === 0) {
          this.$notify({
            type: "warning",
            group: "zones",
            title: "No Shapes Found",
            text: `No ${this.selectedShapeType} shapes found in the current region`,
            duration: 3000,
          });
        } else {
          this.$notify({
            type: "info",
            group: "zones",
            title: "Shape Filter Applied",
            text: notificationText,
            duration: 3000,
          });
        }
      } else {
        const counts = this.getShapeTypeCounts();
        this.$notify({
          type: "info",
          group: "zones",
          title: "Shape Filter Cleared",
          text: `Showing all ${counts.total} shapes`,
          duration: 3000,
        });
      }

      // Update overlapping button visibility based on selected shape type
      // this.updateIconOverlappingButton();
    },
    /**
     * Filters shapes by their type and re-renders the map
     * @param {Event} event - The change event from the select element
     */
    /**
     * Resets the shape type filter to show all shapes
     */
    resetShapeTypeFilter() {
      this.selectedShapeType = "ALL_TYPES";
      this.selectedClients = [];
      if (this.$store.state.region && this.$store.state.region !== "ALL REGIONS") {
        this.renderZonesToMap(false, this.$store.state.region);
      }

      // Update draw control visibility after resetting filter
      this.updateDrawControlVisibility(this.$store.state.region);
    },
    /**
     * Gets the count of shapes by type for the current region
     * @returns {Object} Object with counts for each shape type
     */
    getShapeTypeCounts() {
      const allZones = this.$store.state.zoneInformations || [];
      if (allZones.length === 0) {
        return { geofence: 0, distance: 0, billable: 0, payable: 0, total: 0 };
      }

      const counts = {
        geofence: 0,
        distance: 0,
        billable: 0,
        payable: 0,
        total: allZones.length
      };

      allZones.forEach(zone => {
        if (zone.type) {
          counts[zone.type] = (counts[zone.type] || 0) + 1;
        }
      });

      return counts;
    },
    /**
     * Checks if the map is ready and initialized
     * @returns {Boolean} True if map is ready
     */
    isMapReady() {
      return this.map && typeof this.map.setView === 'function' && this.mappingLoaded;
    },
    /**
     * Handles the ALL_CLIENTS selection logic for the multi-select
     * @param {Event} event - The change event from the select element
     */
    handleClientSelection(event) {
      const selectedOptions = Array.from(event.target.selectedOptions).map(option => option.value);

      // If ALL_CLIENTS is selected, clear other selections
      if (selectedOptions.includes('ALL_CLIENTS')) {
        this.selectedClients = ['ALL_CLIENTS'];
      } else {
        // If specific clients are selected, remove ALL_CLIENTS
        this.selectedClients = selectedOptions.filter(option => option !== 'ALL_CLIENTS');

        // If no clients are selected, default to ALL_CLIENTS
        if (this.selectedClients.length === 0) {
          this.selectedClients = ['ALL_CLIENTS'];
        }
      }
    },
    closeClientSelectorModal() {
      this.showClientSelectorModal = false;
      // Reset temp selection to current selection
      this.tempSelectedClients = [...this.selectedClients];
    },
    applyClientFilter() {
      // Apply the temporary selection to the actual selection
      this.selectedClients = [...this.tempSelectedClients];
      this.showClientSelectorModal = false;
      // Trigger the filter application
      this.applyShapeTypeFilter();
    },
    clearClientFilter() {
      this.tempSelectedClients = [];
    },
    openClientSelectorModal() {
      // Initialize temp selection with current selection
      this.tempSelectedClients = [...this.selectedClients];
      this.showClientSelectorModal = true;
      console.log('Button clicked, modal state:', this.showClientSelectorModal);
    },
  },
  computed: {
    ...mapState([
      "region",
      "hubs",
      "lanes"
    ]),
    ...mapGetters(["isDataLoaded"]),
    /**
     * Computed property to show current filter status
     * @returns {String} Current filter status text
     */
    currentFilterStatus() {
      if (this.selectedShapeType === "ALL_TYPES") {
        return "Showing all shape types";
      }
      return `Filtered to show only ${this.selectedShapeType} shapes`;
    },
    /**
     * Computed property to get client options based on selected shape type
     * @returns {Array} Array of client/contractor options
     */
    clientOptions() {
      if (this.selectedShapeType === 'billable') {
        // Return vendors/clients for billable type
        return this.$store.state.vendors?.map(vendor => ({
          label: `${vendor.businessUnit} - ${vendor.subBusinessUnit}`,
          value: vendor.Id
        })) || [];
      } else if (this.selectedShapeType === 'payable') {
        // Return contractors for payable type
        return this.$store.state.contractors?.map(contractor => ({
          label: contractor.company_name || contractor.key || contractor.name,
          value: contractor.id
        })) || [];
      }
      return [];
    }
  },
  async mounted() {
    // Check if geolocation is available
    if (!navigator.geolocation) {
      console.warn("Geolocation is not supported by this browser");
      // Set default coordinates (Toronto)
      this.center = [43.6532, -79.3832];
      return;
    }

    // Try to get current position
    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.center = [position.coords.latitude, position.coords.longitude];
      },
      (error) => {
        console.warn("Geolocation error:", error.message);
        // Set default coordinates (Toronto)
        this.center = [43.6532, -79.3832];
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      }
    );

    // window.addEventListener('wheel', this.handleScroll);
    // this.$tours.myTour.start();
    auth.onAuthStateChanged(async (user) => {
      if (!user) {
        this.$router.push("/");
      } else if (user) {
        await this.$store.dispatch("updateUser", user);
      }
    });

    configurationsCollection.onSnapshot((snapshot) => {
      const config = snapshot.docs.map((doc) => {
        return {
          Id: doc.id,
          ...doc.data(),
        };
      });
      this.$store.dispatch("setConfigurations", config);
    });

    // Set up the hubs listener to fetch ALL hubs without region filtering
    this.distzoneListenerUnsubscribe = hubsCollection
      .onSnapshot((snapshot) => {
        const allHubs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        this.$store.dispatch("setHubs", allHubs);
      });

    // Set up the lanes listener
    this.lanesListenerUnsubscribe = lanesCollection
      .onSnapshot((snapshot) => {
        const lanes = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        this.$store.dispatch("setLanes", lanes);
      });
    await this.$store.dispatch("onLoad");
    await this.$store.dispatch("initialize");

    // Load contractors for modal components
    try {
      const contractors = await fetchContractors();
      this.$store.dispatch("setContractors", contractors);
    } catch (error) {
      console.error('Error loading contractors:', error);
    }

    const currentEnvironment = this.$router.currentRoute.name;
    this.$store.dispatch("setEnvironment", currentEnvironment);
    this.showChangingRegionModal = true;
    this.mappingLoaded = true;
    this.map = this.$refs.map.mapObject;

    // Ensure map is initialized before proceeding
    if (!this.map) {
      console.error('Map not initialized');
      return;
    }

    const mapContainer = this.map.getContainer();
    mapContainer.addEventListener("wheel", this.handleWheel, {
      passive: false,
    });

    this.spiderfy = new OverlappingMarkerSpiderfier(this.map, {
      nearbyDistance: 5,
    });
    // add listener to prevent popup from showing when click the one of the overlapping markers
    this.spiderfy.addListener("spiderfy", () => {
      this.map.closePopup();
      this.revertSelectedHubMarker(true);
    });
    window.map = this.map;
    const drawnItems = new window.L.FeatureGroup();
    this.drawnItems = drawnItems;
    const FSAItems = new window.L.FeatureGroup();
    this.FSAItems = FSAItems;
    const drawnMarkers = new window.L.FeatureGroup();
    const drawnMarkersPostalCodes = new window.L.FeatureGroup();
    const markerGroup = window.L.markerClusterGroup({
      spiderfyOnMaxZoom: false,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: false,
      chunkedLoading: true,
      animate: false,
      maxClusterRadius: 100,
      iconCreateFunction: () => {
        return window.L.divIcon({ html: "", className: "marker-cluster-icon" });
      },
    });
    const markerGroupFSA = window.L.markerClusterGroup({
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: true,
      zoomToBoundsOnClick: true,
      chunkedLoading: true,
      animate: true,
      maxClusterRadius: 50,
    });
    this.markerGroup = markerGroup;
    this.markerGroupFSA = markerGroupFSA;
    this.postalCodesGroup = drawnMarkersPostalCodes;
    // Define you draw handler somewhere where you click handler can access it. N.B. pass any draw options into the handler
    this.map.addLayer(this.drawnItems);
    this.map.addLayer(drawnMarkers);
    this.map.addLayer(drawnMarkersPostalCodes);
    this.map.addLayer(markerGroup);
    // this.map.addLayer(markerGroupFSA)
    // toolbar for polygon
    window.L.EditToolbar.Delete.include({
      removeAllLayers: false,
    });

    // Create regular draw control
    // Create custom draw control that includes overlapping toggle
    const customDrawControl = this.createCustomDrawControl();
    this.drawControl = customDrawControl;
    this.map.addControl(this.drawControl);

    // this control is for add or deleting shapes in a zone
    const addDeletePartShapeModeDrawControl = new window.L.Control.Draw({
      edit: {
        featureGroup: this.drawnItems,
        edit: false,
        remove: false,
      },
      draw: {
        polygon: {
          shapeOptions: {
            clickable: true,
            color: "#007D6A",
          },
          allowIntersection: this.allowOverlappingShapes, // Use the toggle state
        },
        rectangle: {
          shapeOptions: {
            clickable: true,
            color: "#ED184F",
          },
        },
        circle: false,
        polyline: false, // Explicitly disable the polyline drawing tool
        marker: false,
        circlemarker: false,
      },
      position: "topright",
    });
    this.addDeletePartShapeModeDrawControl = addDeletePartShapeModeDrawControl;

    // Initialize draw control visibility based on current region
    this.updateDrawControlVisibility();

    const provider = new GoogleProvider({
      apiKey: "AIzaSyD8O0Tq7WeY3zAQUl3crewR54CVBkVEq9A",
    });

    const searchControl = new SearchControl({
      style: "button",
      provider,
      searchLabel: "Search for a postal code",
      notFoundMessage: "No results were found",
      showMarker: false,
      showPopup: false,
    });

    this.map.addControl(searchControl);
    const vm = this;
    this.map.on("geosearch/showlocation", function (e) {
      const { location } = e;
      const { x, y, raw, label } = location;
      const lat = y;
      const lng = x;

      const addressComponents = {
        address: label,
        postalCode: raw.address_components[0].long_name,
      }
      const { marker, instance } = vm.createPostalCodeMarker(lat, lng, addressComponents);

      const _contentPopup = window.L.popup({
        className: "postalcode-content-popup",
        closeButton: true,
        autoPan: true,
      });

      _contentPopup.setContent(instance.$el);

      // Bind the popup to the marker and open it immediately
      marker.bindPopup(_contentPopup);
      marker.openPopup();

      // Add marker to the appropriate layer groups
      drawnMarkers.addLayer(marker);
      vm.postalCodesGroup.addLayer(marker);
      vm.drawnMarkersPostalCodes.push({
        leafletId: marker._leaflet_id,
        postalCode: addressComponents.postalCode
      });

      if (vm.spiderfy) {
        vm.spiderfy.addMarker(marker);
      }

      // Center the map on the new marker with a slight delay to ensure popup renders properly
      setTimeout(() => {
        vm.centerMapWithOffset(lat, lng);
      }, 100);
    });

    try {
      workerExportPostalCode.addEventListener("message", async (event) => {
        if (event.data.type === "export") {
          const { data: blob } = event.data;
          const fileStream = streamSaver.createWriteStream(
            `zones-postalcode.xlsx`,
            {
              size: blob.size, // Makes the percentage visible in the download
            }
          );
          const readableStream = blob.stream();
          // more optimized pipe version
          // (Safari may have pipeTo but it's useless without the WritableStream)
          if (window.WritableStream && readableStream.pipeTo) {
            return readableStream
              .pipeTo(fileStream)
              .then(() => console.log("done writing"));
          }
          // Write (pipe) manually
          const writer = fileStream.getWriter();
          const reader = readableStream.getReader();
          const pump = () =>
            reader
              .read()
              // @ts-ignore
              .then((res) =>
                res.done ? writer.close() : writer.write(res.value).then(pump)
              );
          pump();
          this.showChangingRegionModal = false;
          this.importingLoading = false;
          this.$notify({
            type: "success",
            group: "zones",
            title: "Export File Success",
          });
        }
      });
      workerAddMoreShape.addEventListener("message", async (event) => {
        if (event.data.type === "addMoreShapeOverlapping") {
          const { removeOverlapCoordinates, selectedShape, shapesOverlaped } =
            event.data;
          const currentSelectedShape = await findOne("zones", {
            _id: selectedShape.id,
          });
          const currentSelectedShapeCoordinates = currentSelectedShape.multi
            ? JSON.parse(currentSelectedShape.coordinates).map((shape) => {
              return shape.map((c) => [c[1], c[0]]);
            })
            : JSON.parse(currentSelectedShape.coordinates).map((c) => [
              c[1],
              c[0],
            ]);
          const currentSelectedShapeInvertedOrderCoordinates = JSON.parse(
            currentSelectedShape.coordinates
          );
          const selectedShapeUpdated = {
            ...currentSelectedShape,
            coordinates: currentSelectedShapeCoordinates,
            invertedCoordinates: currentSelectedShapeInvertedOrderCoordinates,
            shapeId: currentSelectedShape._id.toString(),
            id: currentSelectedShape._id.toString(),
          };
          workerAddMoreShapeOverlapping.postMessage({
            selectedShape: selectedShapeUpdated,
            overlapCoordinates: removeOverlapCoordinates,
            drawnFSA: this.drawnFSA,
            shapesOverlaped,
          });
        } else if (event.data.type === "callEditZoneCoordinates") {
          const { combineCoordinates, shapeId, multi, fsa, distributionZoneId } = event.data;
          await this.pushToQueue("EDIT_COORDINATES", {
            coordinates: combineCoordinates,
            shapeId,
            multi,
            fsa,
            distributionZoneId,
          });
        } else if (event.data.type === "errorNotificatation") {
          this.onEditProcess = false;
          this.$notify({ group: "zones", clean: true });
          this.$notify({
            type: "error",
            group: "zones",
            title: "Please Try Again",
          });
        }
        this.closeAddDeletePartShapeMode();
      });
      workerDeletePartShapeMode.addEventListener("message", async (event) => {
        console.log("event.data", event.data)
        if (event.data.type === "callEditZoneCoordinates") {
          const { combineCoordinates, shapeId, multi, fsa, distributionZoneId } = event.data;
          await this.pushToQueue("EDIT_COORDINATES", {
            coordinates: combineCoordinates,
            shapeId,
            multi,
            fsa,
            distributionZoneId,
          });
        } else if (event.data.type === "errorNotificatation") {
          this.$notify({ group: "zones", clean: true });
          this.$notify({
            type: "error",
            group: "zones",
            title: "Please Try Again",
          });
          this.onEditProcess = false;
          this.closeAddDeletePartShapeMode();
        } else if (event.data.type === "cantDeleteShapeNotification") {
          this.$notify({ group: "zones", clean: true });
          this.$notify({
            type: "error",
            group: "zones",
            title: "Can't delete shape",
            text: "You cannot delete the entire shape",
          });
          this.onEditProcess = false;
        }
        this.closeAddDeletePartShapeMode();
      });
      workerAddMoreShapeOverlapping.addEventListener(
        "message",
        async (event) => {
          if (event.data.type === "callEditZoneCoordinates") {
            const { overlapCoordinates, shapeId, multi, fsa, shapesOverlaped, distributionZoneId } =
              event.data;
            await this.pushToQueue("EDIT_COORDINATES", {
              coordinates: overlapCoordinates,
              shapeId,
              multi,
              fsa,
              distributionZoneId,
            });
            for (const shapesId of shapesOverlaped) {
              const currentSelectedShape = await findOne("zones", {
                _id: shapesId,
              });
              const currentSelectedShapeInvertedOrderCoordinates = JSON.parse(
                currentSelectedShape.coordinates
              );
              const { shapesID, multi, distributionZoneId, zoneClients, type, daysOfService } = currentSelectedShape;
              await updatePostalCode(
                currentSelectedShapeInvertedOrderCoordinates,
                shapesID,
                multi,
                distributionZoneId,
                zoneClients,
                [],
                daysOfService || {},
                type || "geofence"
              );
            }
          } else if (event.data.type === "insertZone") {
            const { shapesID, coordinates, multi, fsa, distributionZoneId, zoneClients, type, daysOfService } = event.data;
            this.pushToQueue("INSERT_NEW_SHAPE", {
              shapesID,
              coordinates,
              multi,
              fsa,
              distributionZoneId,
              zoneClients,
              type: type || "geofence",
              daysOfService: daysOfService || {}
            });
          } else if (event.data.type === "deleteZone") {
            const { selectedShapeId } = event.data;
            this.pushToQueue("DELETE_SHAPE", {
              shapeId: selectedShapeId,
            });
          } else if (event.data.type === "errorNotificatation") {
            this.$notify({ group: "zones", clean: true });
            this.$notify({
              type: "error",
              group: "zones",
              title: "Please Try Again",
            });
            this.closeAddDeletePartShapeMode();
            this.onEditProcess = false;
          } else if (event.data.type === "done") {
            this.onEditProcess = false;
          } else if (event.data.type === "updateOverlapShapes") {
            const { shapesOverlaped } = event.data;
            for (const shapesId of shapesOverlaped) {
              const currentSelectedShape = await findOne("zones", {
                _id: shapesId,
              });
              const currentSelectedShapeInvertedOrderCoordinates = JSON.parse(
                currentSelectedShape.coordinates
              );
              const { shapesID, multi, distributionZoneId, zoneClients } = currentSelectedShape;
              await updatePostalCode(
                currentSelectedShapeInvertedOrderCoordinates,
                shapesID,
                multi,
                distributionZoneId,
                zoneClients,
                []
              );
            }
          }
        }
      );
      workerAddShape.addEventListener("message", async (event) => {
        if (event.data.type === "renderNewZoneAddToModal") {
          const { coordinates, shapesToAdd, shapeType } = event.data;
          this.renderNewZoneToAddModal(coordinates, shapesToAdd, shapeType);
        } else if (
          event.data.type === "renderNewZoneAddToModalNotIntersecting"
        ) {
          const { coordinates, coordinateForLeaflet } = event.data;
          const layer = window.L.polygon(coordinateForLeaflet, {
            color: "black",
            fillOpacity: 0.6,
            weight: 0.5,
          });
          // layer = newCircle;
          this.drawnItems.addLayer(layer);
          const ComponentClass = Vue.extend(AddShapeModal);
          const instance = new ComponentClass({
            propsData: {
              coordinates: coordinates,
              region: this.$store.state.region,
              selectedShapeTypeFilter: this.selectedShapeType,
            },
            methods: {
              close: () => {
                this.map.closePopup();
                if (this.map._layers[layer._leaflet_id]) {
                  // Remove from drawnItems group instead of directly from map
                  this.map.removeLayer(layer);
                  this.drawnItems.removeLayer(layer);

                  // TODO: need to find a better way without re-rendering the whole map
                  // this.renderZonesToMap(false, this.$store.state.region);
                }
              },
              updateMap: async (props) => {
                const { coordinates, zoneId, distributionZoneId, zoneClients, selectedType, daysOfService, color } = props;
                this.pushToQueue("INSERT_NEW_SHAPE", {
                  shapesID: zoneId,
                  coordinates,
                  multi: false,
                  fsa: [],
                  distributionZoneId,
                  zoneClients,
                  type: selectedType || "geofence",
                  daysOfService: daysOfService || {},
                  color: selectedType === 'geofence' ? color : null
                });
              },
            },
          });
          instance.$mount();
          const contentPopup = instance.$el;
          const popupCoords = layer.getCenter();
          window.L.popup({
            autoPan: false,
            keepInView: true,
            closeButton: true,
            autoClose: true,
            closeOnClick: false,
            className: 'add-shape-modal-popup' // Add custom class for AddShapeModal
          })
            .setLatLng([popupCoords.lat, popupCoords.lng])
            .setContent(contentPopup)
            .openOn(this.map);
          layer.bindPopup(contentPopup);
          this.$notify({
            group: "zones",
            clean: true,
          });
        }
        this.$notify({ group: "zones", clean: true });
        this.$notify({
          type: "success",
          group: "zones",
          title: "Processing...",
          text: "Map Drawn",
          duration: 5000,
        });
      });
      workerSelectFSA.addEventListener("message", async (event) => {
        //
        if (event.data.type === "addFSAToShape") {
          const { shape } = event.data;
          this.selectedFSAToAdd.push(shape);
          this.renderSelectedFSAShape();
        } else if (event.data.type === "error") {
          this.onEditProcess = false;
          this.$notify({
            type: "error",
            group: "zones",
            title: "There is an overlap",
            text: "The FSA you selected overlaps a existing zone",
          });
        }
      });
      workerCombineSelectedFSA.addEventListener("message", async (event) => {
        if (event.data.type === "renderFSAZoneToModal") {
          const { coordinates, shapesToAdd, shapeType, mode } = event.data;
          if (mode === "ADD") {
            this.renderFSAZoneToAddModal(coordinates, shapesToAdd, shapeType);
          } else {
            this.renderFSAZoneToEditModal(coordinates, shapesToAdd, shapeType);
          }
        } else {
          this.$notify({
            type: "warn",
            group: "zones",
            title: "There is an error",
            text: "Please try again",
          });
        }
      });
    } catch (error) {
      console.log("error:", error);
    }
    // on select shape
    this.drawnItems.on("click", (e) => {
      const selectedShapeId = e.sourceTarget._leaflet_id;
      const selectedShape = this.drawnZones.find(
        (d) => d.leafletId === selectedShapeId
      );
      if (
        selectedShape &&
        !this.editShapeSelectedMode &&
        !this.addMoreShapeMode &&
        !this.deletePartShapeMode &&
        !this.selectableFSAMode &&
        !this.editSelectableFSAMode
      ) {
        this.selectedShape = selectedShape;
      }
    });

    this.setupDrawInteractivityControl();

    this.map.on("draw:created", async (e) => {
      const layer = e.layer;
      // check if the mode is in delete or adding more shapes to a zone or
      // adding parts zone
      // delete parts zone
      // add new zone or marker
      if (this.addMoreShapeMode) {
        this.$notify({
          group: "zones",
          title: "Processing...",
          text: "Updating Zone, please wait",
          closeOnClick: false,
          duration: 10000,
        });
        try {
          const selectedShape = this.selectedShape;
          const coordinates = layer.getLatLngs();
          if (this.onEditProcess) {
            this.$notify({
              group: "zones",
              clean: true,
            });
            this.$notify({
              type: "warn",
              group: "zones",
              title: "Currently process edit shape",
              text: "Please wait for it to be finish",
            });
            this.closeAddDeletePartShapeMode();
            return;
          }
          if (!selectedShape) {
            this.$notify({
              type: "warn",
              group: "zones",
              title: "There is an error",
              text: "Please try again",
            });
            this.closeAddDeletePartShapeMode();
            return;
          }
          this.onEditProcess = true;
          this.pushToQueue("PROCESS_EDITING_ADD", {
            selectedShape,
            coordinates,
            drawnZones: this.drawnZones,
          });
          this.closeAddDeletePartShapeMode();
        } catch (err) {
          console.error("err:", err);
          this.$notify({
            type: "warn",
            group: "zones",
            title: "Error.",
            text: "There is something wrong on updating zone, please try again.",
          });
          this.closeAddDeletePartShapeMode();
        }
      } else if (this.deletePartShapeMode) {
        this.$notify({
          group: "zones",
          title: "Processing...",
          text: "Updating Zone, please wait",
          closeOnClick: false,
          duration: -1,
        });
        try {
          const coordinates = layer.getLatLngs();
          const selectedShape = this.selectedShape;
          if (this.onEditProcess) {
            this.$notify({
              group: "zones",
              clean: true,
            });
            this.$notify({
              type: "warn",
              group: "zones",
              title: "Currently process edit shape",
              text: "Please wait for it to be finish",
            });
            this.closeAddDeletePartShapeMode();
            return;
          }
          if (!selectedShape) {
            this.$notify({
              type: "warn",
              group: "zones",
              title: "There is an error",
              text: "Please try again",
            });
            this.closeAddDeletePartShapeMode();
            return;
          }
          setTimeout(async () => {
            this.onEditProcess = true;
            this.pushToQueue("PROCESS_EDITING_REMOVE", {
              selectedShape,
              coordinates,
              drawnZones: this.drawnZones,
            });
            this.closeAddDeletePartShapeMode();
          }, 500);
        } catch (err) {
          console.log("err:", err);
          this.$notify({
            type: "warn",
            group: "zones",
            title: "Error.",
            text: "There is something wrong on updating zone, please try again.",
          });
          this.closeAddDeletePartShapeMode();
        }
      } else {
        /* If it's a marker being added */
        if (e.layerType === "marker") {
          const { lat, lng } = layer._latlng;
          // Store reference to the original drawing marker layer for cleanup
          this.map.removeLayer(layer);
          const { marker, instance } = this.createMarker(lat, lng, true);

          const _contentPopup = window.L.popup({
            className: "hub-content-popup",
            closeButton: true,
            autoPan: true,
          })

          _contentPopup.setContent(instance.$el);

          // Bind the popup to the marker and open it immediately
          marker
            .bindPopup(_contentPopup)
            .openPopup();

          drawnMarkers.addLayer(marker);
          this.spiderfy.addMarker(marker);

          // Center the map on the new marker
          this.centerMapWithOffset(lat, lng);
        } else {
          let coordinates = null;
          let coordinateForLeaflet = null;
          // this.drawnItems.addLayer(layer);
          if (e.layerType === "circle") {
            const latLng = layer.getLatLng();
            const radius = layer.getRadius(); // in m

            // Round the radius to the nearest kilometer
            const radiusInKm = radius / 1000;
            const roundedRadiusInKm = Math.round(radiusInKm);
            const roundedRadiusInMeters = roundedRadiusInKm * 1000;
            // Update the layer with the rounded radius
            layer.setRadius(roundedRadiusInMeters);
            const center = [latLng.lng, latLng.lat];
            const circle = circleTurf(center, roundedRadiusInKm);
            coordinates = circle.geometry.coordinates[0];
            coordinateForLeaflet = coordinates.map((c) => [c[1], c[0]]);
            coordinateForLeaflet.push([
              coordinateForLeaflet[0][0],
              coordinateForLeaflet[0][1],
            ]);
          } else {
            if (e.layerType === 'polyline') {
              // Get coordinates of the drawn polyline
              const latLngs = layer.getLatLngs();
              const coordinates = latLngs.map(latlng => [latlng.lat, latlng.lng]);
              const hubIds = this.selectedHubIds || [];
              if (hubIds.length >= 2) {
                // popup the LaneWizard modal
                this.laneWizardHubIds = [...hubIds];
                this.laneWizardData = null;
                this.showLaneWizard = true;
              } else {
                // Remove the polyline if not enough hubs were selected
                this.$notify({
                  type: "error",
                  group: "zones",
                  title: "Invalid Lane",
                  text: "Lane must connect at least 2 hub markers."
                });
              }

              this.map.removeLayer(layer);
              // Reset the selected hub IDs
              this.selectedHubIds = [];
              return;
            } else {
              /* Not sure if it's used at all */
              coordinates = layer.getLatLngs()[0].map((c) => [c.lng, c.lat]);
              coordinates.push([coordinates[0][0], coordinates[0][1]]);
              coordinateForLeaflet = coordinates.map((c) => [c[1], c[0]]);
              coordinateForLeaflet.push([
                coordinateForLeaflet[0][0],
                coordinateForLeaflet[0][1],
              ]);
            }
          }

          if (coordinates.length <= 0) {
            return;
          }
          const coods = coordinates;
          this.$notify({
            group: "zones",
            title: "Processing...",
            text: "Currently draw shape to the map, please wait",
            closeOnClick: false,
            duration: -1,
          });
          return workerAddShape.postMessage({
            coordinates: coods,
            coordinateForLeaflet,
            drawnZones: this.drawnZones,
            drawnFSA: this.drawnFSA,
            allowOverlapping: this.allowOverlappingShapes, // Pass the toggle state to the worker
          });
        }
      }
      this.closeAddDeletePartShapeMode();
    });
    // on edit save/stop
    this.map.on("draw:editstop", () => {
      // if the user save or cancel, this must be updated
      this.editedZones.map((zone) => {
        const drawnZone = this.drawnZones.find(
          (o) => o.leafletId === zone.leafletId
        );
        const coordinates = drawnZone.coordinates.map((c) => [c[1], c[0]]);
        const instance = EditShape(
          this.$store,
          coordinates,
          zone,
          this.vehicles,
          this.map,
          true /* editable */,
          this.editCurrentShapeModeOn,
          this.renderZonesToMap,
          this.activateEditSelectableFSAMode,
          this.closeEditCurrentShapeMode
        );
        instance.$mount();
        const contentPopup = instance.$el;
        this.map._layers[zone.leafletId].setPopupContent(contentPopup);
      });
    });
    // on edit move vertex
    this.map.on("draw:editvertex", (e) => {
      const poly = e.poly;
      const latlngs = poly.getLatLngs()[0].map((c) => [c.lng, c[0]]);
      latlngs.push([latlngs[0][0], latlngs[0][1]]);
      const leafletId = poly._leaflet_id;
      const drawnZone = this.drawnZones.find((o) => o.leafletId === leafletId);
      if (!this.editedZones.find((o) => o.leafletId === leafletId)) {
        this.editedZones.push({
          leafletId,
          zone: drawnZone.zone,
          coordinates: latlngs,
          id: drawnZone.id,
          multi: drawnZone.multi,
        });
      } else {
        const editedDrawnZoneIndex = this.editedZones.findIndex(
          (o) => o.leafletId === leafletId
        );
        this.editedZones[editedDrawnZoneIndex].coordinates = latlngs;
      }
      const instance = EditShape(
        this.$store,
        latlngs,
        drawnZone,
        this.vehicles,
        this.map,
        true /* editable */,
        this.editCurrentShapeModeOn,
        this.renderZonesToMap,
        this.activateEditSelectableFSAMode,
        this.closeEditCurrentShapeMode
      );
      instance.$mount();
      const contentPopup = instance.$el;
      poly.setPopupContent(contentPopup);
    });
    // on delete
    this.map.on("draw:deleted", async (e) => {
      this.$notify({
        group: "zones",
        clean: true,
      });
      console.log("delete shape called in map", e.layers._layers)
      const deleteLayersId = Object.keys(e.layers._layers).map(Number);
      try {
        Promise.all(
          deleteLayersId.map(async (id) => {
            console.log("delete layer id", id)
            console.log("drawn zones", this.drawnZones)
            const drawnZone = this.drawnZones.find((o) => o.leafletId === id);

            if (!drawnZone) {
              // remove layer from the leaflet
              this.map.removeLayer(e.layers._layers[id]);
              return;
            }

            console.log("drawn zone", drawnZone)
            // remove draw zone from the leaflet
            this.drawnZones = this.drawnZones.filter((o) => o.leafletId !== id);
            /* todo: whats this? */
            // const regions = this.regions;
            // when removing zone, you must remove the assigned zone from the truck
            // regions.map(async (reg, index) => {
            //   const selectedTruck = this.vehicleObj[this.regions[index]]
            //     .map((t, index) => ({ ...t, truckIndex: index }))
            //     .filter((truck) => truck.zone === drawnZone.zone);
            //   selectedTruck.map((truck) => {
            //     const payload = {
            //       value: "",
            //       key: "zone",
            //       index: truck.truckIndex,
            //       region: reg,
            //     };
            //     this.$store.dispatch("setTruckFleetSetting", payload);
            //   });
            //   await fleetSettings
            //     .doc(`${reg}`)
            //     .set({ fleet: this.$store.state.fleetSettings[reg] });
            // });
            // if drawnZone.shpeId is null, then just remove the drawn shape
            if (!drawnZone.shapeId) {
              this.drawnZones = this.drawnZones.filter((o) => o.leafletId !== id);
              return;
            }

            this.$notify({
              group: "zones",
              title: "Processing",
              closeOnClick: false,
              duration: -1,
              text: "Data processing, please wait...",
            });

            console.log("delete shape called in map", drawnZone.shapeId)
            this.pushToQueue("DELETE_SHAPE", {
              shapeId: drawnZone.shapeId,
            });
          })
        );
      } catch (error) {
        console.error("error deleting shape in map", error)
      }
    });

    // Track when drawing starts
    this.map.on('draw:drawstart', (e) => {
      this.currentDrawing = e.layerType;
      this.selectedHubIds = [];
    });

    // Clean up when drawing stops
    this.map.on('draw:drawstop', () => {
      this.currentDrawing = null;
      if (this.tempHubMarker) {
        this.map.removeLayer(this.tempHubMarker);
        this.tempHubMarker = null;
      }
    });

    try {
      // TODO: refactor all this to be processed before the map is loaded
      sharedViewsCollection.orderBy("name").onSnapshot(async (snapshot) => {
        const sharedViews = await snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        await this.$store.dispatch("setSharedViews", sharedViews);
      });

      // TODO & TBD: set FSA name to be same with province name
      const provinceFSAregionName = {
        Alberta: "AB",
        "British Columbia": "BC",
        Manitoba: "MB",
        "New Brunswick": "NB",
        "Newfoundland and Labrador": "NL",
        "Nova Scotia": "NS",
        "Northwest Territories": "NT",
        Nunavut: "NU",
        Ontario: "ON",
        "Prince Edward Island": "PE",
        Quebec: "QC",
        Saskatchewan: "SK",
        Yukon: "YT",
      }

      regionsCollection.orderBy("name").onSnapshot(async (snapshot) => {
        const regionsData = await snapshot.docs.map((doc) => ({
          ...doc.data(),
          fsaRegion: provinceFSAregionName[doc.data().province],
        }));
        this.$store.dispatch("setRegions", regionsData);
        this.regions = regionsData;
        if (!this.initialLoadRegion) {
          try {
            // Try to get user's location
            // const position = await this.getUserLocation();
            // this.center = [position.coords.latitude, position.coords.longitude];
            // this.map.setView(this.center, 13); // Zoom level 13 is just an example, adjust as needed
            this.initialLoadRegion = true;
          } catch (error) {
            // Fall back to original logic if geolocation fails
            if (this.regions.length > 0) {
              this.showRegionList = false;
              this.center = regionCenter(this.regions[0].name);
              this.$store.dispatch("setRegion", this.regions[0].id);
              // this.renderZonesToMap(true, this.regions[0].id);
            } else {
              this.showRegionList = true;
            }
            this.initialLoadRegion = true;
          }
        }
        if (regionsData.length <= 0) {
          this.showRegionList = true;
          return;
        }
        snapshot.docChanges().forEach((change) => {
          if (change.type === "added") {
            // if regions was empty at first and new one region added, then load the first region
            if (regionsData.length === 1) {
              this.$store.dispatch("setRegion", regionsData[0].id);
              // this.renderZonesToMap(true, regionsData[0].id);
            }
          }
          if (change.type === "modified") {
            // this will call because adding region will set region
            if (regionsData.length === 1) {
              this.$store.dispatch("setRegion", regionsData[0].id);
              this.renderZonesToMap(true, regionsData[0].id);
            }
          }
          if (change.type === "removed") {
            if (this.$store.state.region === change.doc.data().id) {
              if (regionsData.length > 0) {
                this.$store.dispatch("setRegion", regionsData[0].id);
                this.renderZonesToMap(true, regionsData[0].id);
              } else {
                this.$store.dispatch("setRegion", "");
                this.showRegionList = true;
              }
            }
          }
        });
      });

      zonesInfoCollection
        .where("environment", "==", this.$store.state.environment.toLowerCase())
        .onSnapshot((snapshot) => {
          const zoneInformations = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          this.$store.dispatch("setZoneInformations", zoneInformations);
        });

      vendorsCollection.orderBy("businessUnit").onSnapshot((snapshot) => {
        const vendorsData = snapshot.docs.map((doc) => ({
          Id: doc.id,
          ...doc.data(),
        }));
        this.$store.dispatch("setVendors", vendorsData);
      });
      // Add lanes listener
      this.lanesListenerUnsubscribe = lanesCollection.onSnapshot((snapshot) => {
        const lanes = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        this.$store.dispatch("setLanes", lanes);
      });

      globalUnitsCollection.onSnapshot((snapshot) => {
        const globalDistanceUnit = snapshot.docs.find(
          (doc) => doc.id === "Distance"
        );
        const dataSourceUnit = "km";
        const globalUnit = {
          selected: globalDistanceUnit.data().selected || dataSourceUnit,
          dataSourceUnit: dataSourceUnit,
        };

        this.$store.dispatch("setGlobalDistanceUnit", globalUnit);
      });

      taxProvincesCollection.onSnapshot((snapshot) => {
        const taxProvinces = snapshot.docs.map((doc) => ({
          ...doc.data(),
        })).sort((a, b) => a.Province.localeCompare(b.Province));
        this.$store.dispatch("setTaxProvinces", taxProvinces);
      });
    } catch (error) {
      console.log("error:", error);
    }
    await this.renderFSAToMap("");
    this.$store.dispatch("setRegion", "ALL REGIONS");
    // Add custom polyline control
    this.initCustomPolylineControl();

    // Set up Firestore listener for MAPPING_SHAPES collection
    this.setupMappingShapesListener();
  },
};
</script>
<style lang="scss">
@import "https://unpkg.com/leaflet-geosearch@2.6.0/assets/css/leaflet.css";
@import "../assets/theme/color_variables";
/* FSA name layer */
@import "../../node_modules/leaflet.markercluster/dist/MarkerCluster.Default.css";

.leaflet-control-geosearch.active form {
  display: flex;
  flex-wrap: wrap;
  min-width: 250px;
}

.leaflet-control-geosearch.active .glass {
  flex: 1;
}

.leaflet-control-geosearch.active .reset {
  border: none;
  background: none;
}

.leaflet-control-geosearch .results {
  width: 100%;
}

.leaflet-control-geosearch .results.active {
  padding: 8px 0;
  border-top: 1px solid #c6c6c6;
}

.leaflet-control-geosearch .results .leaflet-bar-notfound {
  padding: 8px 0;
  border-top: 1px solid #c6c6c6;
}

/* customizing the circles for FSA name layer */
.marker-cluster>* {
  opacity: 0.2 !important;
  background-color: $success !important;
  color: white !important;
}

.leaflet-popup-content-wrapper {
  width: 53vw;
}

.FSA-Content>.leaflet-popup-content-wrapper {
  width: 8vw;
}

.leaflet-popup-content {
  width: 90% !important;
}

.FSA_tooltip {
  /* background: transparent; */
  border: none;
  font-weight: 600;
  font-style: italic;
}

/* esri search control */
.geocoder-control-input {
  border: 2px solid $success !important;
  font-family: "Poppins", sans-serif !important;
}

.leaflet-control-zoom-in,
.leaflet-control-zoom-out,
.leaflet-draw-draw-polygon,
.leaflet-draw-draw-rectangle,
.leaflet-draw-draw-circle,
.leaflet-draw-draw-marker,
.leaflet-draw-section,
.leaflet-control-layers,
.leaflet-control-layers-expanded {
  color: $success !important;
}

.navbar-item {
  color: $success !important;
}

/* Custom styles for lane popups */
.lane-custom-popup {
  .leaflet-popup-content-wrapper {
    padding: 10px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
    width: auto !important;
  }

  .leaflet-popup-content {
    margin: 0;
    width: auto !important;
    min-width: 0;
  }

  .leaflet-popup-tip-container {
    margin-top: -1px;
  }
}

.hub-content-popup {
  .leaflet-popup-content-wrapper {
    overflow: hidden;
    max-width: 700px;
    justify-items: center;
  }

  .leaflet-popup-close-button {
    position: absolute;
    top: 5px;
    right: 5px;
    z-index: 30;
    background-color: white;
    border-radius: 50%;
    width: 25px;
    height: 25px;
    cursor: pointer;
    padding: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid #ddd;
    font-size: 18px;
    line-height: 18px;
    font-weight: bold;
    color: #333;
  }

  .pac-container {
    z-index: 1050 !important;
    /* Ensure it's above the modal */
  }
}

.postalcode-content-popup {
  .leaflet-popup-content-wrapper {
    overflow: hidden;
    max-width: 550px;
    justify-items: center;
  }

  .leaflet-popup-close-button {
    position: absolute;
    top: 5px;
    right: 5px;
    z-index: 30;
    background-color: white;
    border-radius: 50%;
    width: 25px;
    height: 25px;
    cursor: pointer;
    padding: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid #ddd;
    font-size: 18px;
    line-height: 18px;
    font-weight: bold;
    color: #333;
  }

  .pac-container {
    z-index: 1050 !important;
    /* Ensure it's above the modal */
  }
}

/* Custom styling for zone tooltips */
.zone-tooltip .leaflet-tooltip-content {
  white-space: nowrap !important;
  text-align: center;
  line-height: 1.2;
  font-size: 11px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

.zone-tooltip {
  border: none !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  background-color: transparent !important;
  z-index: 1000 !important;
  transition: opacity 0.2s ease-in-out;
  opacity: 0.9;
  /* Slightly transparent by default */
  pointer-events: none;
  /* Prevent tooltip from intercepting mouse events */
}

/* Hide the tooltip arrow by default */
.zone-tooltip:before {
  display: none !important;
}

.zone-tooltip .tooltip-content {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background-color: rgba(255, 255, 255, 0.95);
  /* More opaque background */
  padding: 3px 8px;
  /* Slightly larger padding for better visibility */
  border-radius: 4px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
  /* Stronger shadow for better visibility */
  border: 1px solid rgba(0, 0, 0, 0.2);
  /* More visible border */
}

.zone-tooltip .zone-name {
  font-style: italic;
}

.zone-tooltip .separator {
  opacity: 0.7;
}

.zone-tooltip .hub-name {
  font-weight: bold;
}

/* Add this to your style section */
.zone-tooltip.other-tooltip {
  margin-top: -5px;
  margin-left: -5px;
}

/* Custom styling for hub tooltips */
.hub-tooltip {
  background-color: rgba(255, 255, 255, 0.9);
  border: 1px solid #007D6A;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: bold;
  color: #007D6A;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  white-space: nowrap;
  z-index: 1000;
}

/* Hide the tooltip arrow */
.hub-tooltip:before {
  display: none !important;
}

/* Custom polyline drawing mode styles */
.hub-marker-selected {
  z-index: 2000 !important;
  transition: all 0.2s ease-in-out;
  background-color: black;
  border-radius: 50%;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
  width: 32px;
  height: 32px;
  box-sizing: border-box;
}

/* Make the SVG icon white and centered */
.hub-marker-selected svg {
  fill: white !important;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* Fix the my-custom-pin class to ensure proper centering */
.my-custom-pin.hub-marker-selected {
  display: block;
  text-align: center;
}

.custom-polyline-actions {
  display: flex;
  padding: 5px;
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 4px;
}

.save-polyline-button,
.cancel-polyline-button {
  border-radius: 4px;
  margin: 0 2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.custom-polyline-button.active {
  background-color: black !important;
  color: white !important;
}

/* Overlapping toggle control styling */
.leaflet-overlapping-toggle-control {
  margin-bottom: 10px !important;
}

.overlapping-toggle-container {
  background-color: white;
  padding: 5px 8px;
  border-radius: 4px;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.4);
  font-size: 12px;
  min-width: 140px;
  border: 1px solid #ccc;
}

.overlapping-checkbox {
  margin-right: 5px;
  cursor: pointer;
}

.overlapping-label {
  color: #007D6A;
  font-weight: bold;
  cursor: pointer;
  user-select: none;
}

.overlapping-toggle-container:hover {
  background-color: #f8f9fa;
}

/* Overlapping toggle integrated in draw control */
.overlapping-toggle-in-draw {
  margin-top: 5px !important;
  border-top: 1px solid #ccc !important;
  padding-top: 5px !important;
}

.overlapping-toggle-inner {
  background-color: white;
  padding: 5px 8px;
  border-radius: 4px;
  font-size: 12px;
  min-width: 140px;
  text-align: center;
  border: 1px solid #ccc;
}

.overlapping-toggle-inner:hover {
  background-color: #f8f9fa;
}

/* Icon-based overlapping control styling */
.leaflet-icon-overlapping-control {
  margin: 0 !important;
}

.icon-overlapping-button {
  width: 30px !important;
  height: 30px !important;
  background-color: #fff !important;
  text-align: center !important;
  font-size: 16px !important;
  color: black !important;
  display: block !important;
  border-radius: 4px !important;
  transition: all 0.2s ease !important;
  border: 1px solid #ccc !important;
}

.icon-overlapping-button:hover {
  background-color: #f8f9fa !important;
  transform: scale(1.05) !important;
}

.icon-overlapping-button.active {
  background-color: #007D6A !important;
  color: white !important;
}

/* Custom filter button styling */
.custom-filter-button {
  height: 32px;
  padding: 0 12px;
  background-color: #fff;
  border: 1px solid #000;
  border-radius: 4px;
  color: #000;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  min-width: 120px;
}

.custom-filter-button:disabled {
  background-color: #f5f5f5;
  border-color: #ccc;
  color: #999;
  cursor: not-allowed;
  opacity: 0.6;
}
</style>
<style lang="scss" scoped>
@import "~leaflet/dist/leaflet.css";

.leaflet-control-layers-overlays {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
